"use client"

import React, { useState, useEffect, useMemo, useCallback, useRef } from "react"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { useNavigate, useSearchParams } from "react-router-dom"
import {
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Filter,
  Download,
  Plus,
  MapPin,
  Map as MapIcon,
  Check,
  Minus,
  X,
  Calendar as CalendarIcon,
  RefreshCw,
  Navigation,
  CircleDot,
  Moon,
  Search as SearchIcon,
  RotateCw,
} from "lucide-react"
import { useAuth } from "../context/AuthContext"
import { isWorker } from "../utils/roleUtils"
import { useLocationScope, filterByLocation } from "../context/LocationContext"
import { jobsAPI, teamAPI, territoriesAPI, availabilityAPI, leadsAPI } from "../services/api"
import {
  getWorkingIntervals,
  subtractIntervals,
  sumIntervalMinutes,
  jobIntervalOnDate,
  formatDateKey,
  padIntervals,
  memberDrivingMinutes,
} from "../utils/availabilityMath"
import { normalizeAPIResponse } from "../utils/dataHandler"
import MobileHeader from "../components/mobile-header"
import { useIsMobile } from "../components/ui/use-mobile"
import JobContextMenu from "../components/job-context-menu"
import CreateTaskModal from "../components/create-task-modal"
import DuplicateJobModal from "../components/duplicate-job-modal"
import ConvertToRecurringModal from "../components/convert-to-recurring-modal"
import {
  SfCard,
  SfButton,
  SfKPI,
  SfTag,
  SfTab,
  SfAvatar,
  SfPageHeader,
  sfInitials,
  sfTeamColor,
  sfAssignTeamColors,
  sfJobChipColors,
} from "../components/sf-primitives"

/**
 * Schedule v2 (Wave 5) — Service Blue redesign of /schedule.
 *
 * Tabs: Schedule · Availability · Routes · Unassigned
 * Views (Schedule tab only): Day · Week · Month
 *
 * Plugs into the existing jobsAPI + teamAPI. No new backend.
 */

// ── Helpers (duplicated from dashboard-v2 — kept inline rather than
// abstracted to avoid touching the dashboard's working file) ──────

// Team-member roles that occupy the availability grid for reference but
// are NOT bookable — excluded from KPI aggregates, LB availability
// sync, and any downstream capacity math. Grouped below cleaners in the
// grid render. Module-scope so React's exhaustive-deps lint doesn't
// think the closure captures state.
const NON_BOOKABLE_ROLES = new Set(['account owner', 'owner', 'admin', 'manager', 'scheduler'])
const isBookableTeamMember = (m) =>
  m?.is_service_provider !== false && !NON_BOOKABLE_ROLES.has(String(m?.role || '').toLowerCase())

// Merge date + time parts across candidates in LOCAL time. See dashboard-v2.jsx
// for the full rationale — keep the two copies in sync.
const jobStartDateTime = (job) => {
  const candidates = [job.scheduled_date, job.start_time, job.service_time]
  let y = null, mo = null, d = null, h = null, mi = null, s = null
  let fallback = null

  for (const c of candidates) {
    if (!c) continue
    const raw = String(c).trim()

    const dt = raw.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{1,2}):(\d{2})(?::(\d{2}))?(?:\.\d+)?(Z|[+-]\d{2}:?\d{2})?$/)
    if (dt) {
      if (dt[7]) {
        const parsed = new Date(raw.replace(" ", "T"))
        if (!isNaN(parsed)) return parsed
      }
      if (y === null) { y = +dt[1]; mo = +dt[2] - 1; d = +dt[3] }
      if (h === null) { h = +dt[4]; mi = +dt[5]; s = +(dt[6] || 0) }
      continue
    }

    const dOnly = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/)
    if (dOnly) {
      if (y === null) { y = +dOnly[1]; mo = +dOnly[2] - 1; d = +dOnly[3] }
      continue
    }

    const tOnly = raw.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM|am|pm)?$/)
    if (tOnly) {
      let hh = parseInt(tOnly[1], 10)
      const mer = tOnly[4]?.toUpperCase()
      if (mer === "PM" && hh < 12) hh += 12
      if (mer === "AM" && hh === 12) hh = 0
      if (h === null) { h = hh; mi = +tOnly[2]; s = +(tOnly[3] || 0) }
      continue
    }

    if (!fallback) {
      const parsed = new Date(raw)
      if (!isNaN(parsed)) fallback = parsed
    }
  }

  if (y !== null || h !== null) {
    const now = new Date()
    return new Date(
      y ?? now.getFullYear(),
      mo ?? now.getMonth(),
      d ?? now.getDate(),
      h ?? 0,
      mi ?? 0,
      s ?? 0,
    )
  }
  return fallback
}

const assigneesFor = (job) => {
  const seen = new Set()
  const out = []
  const push = (rawId, name) => {
    const id = rawId == null ? null : String(rawId)
    if (!id || seen.has(id)) return
    seen.add(id)
    out.push({ id, name: name || "" })
  }
  if (Array.isArray(job.assigned_providers)) {
    job.assigned_providers.forEach((p) =>
      push(
        p?.id || p?.team_member_id || p?.provider_id,
        p?.name || `${p?.first_name || ""} ${p?.last_name || ""}`.trim() || p?.email
      )
    )
  }
  if (Array.isArray(job.team_members)) {
    job.team_members.forEach((m) =>
      push(
        m?.id || m?.team_member_id,
        m?.name || `${m?.first_name || ""} ${m?.last_name || ""}`.trim() || m?.email
      )
    )
  }
  if (Array.isArray(job.job_team_assignments)) {
    job.job_team_assignments.forEach((a) =>
      push(a?.team_member_id || a?.id, a?.team_member_name)
    )
  }
  if (Array.isArray(job.team_assignments)) {
    job.team_assignments.forEach((a) =>
      push(a?.team_member_id || a?.id, a?.team_member_name)
    )
  }
  if (job.team_member_id) push(job.team_member_id, job.team_member_name)
  if (job.assigned_to) push(job.assigned_to, job.assigned_to_name)
  return out
}

const isCancelledJob = (j) => {
  const s = String(j?.status || "").toLowerCase()
  return s === "cancelled" || s === "canceled"
}

const isLiveJob = (j) => {
  const s = String(j?.status || "").toLowerCase()
  return s === "in_progress" || s === "in-progress" || s === "in progress" || s === "en_route" || s === "en route"
}

// Calendar status "sign" — icon + color only (not the whole chip fill).
// Matches issue #34: on the way / started / finished.
const normalizeJobStatus = (j) => {
  const s = String(j?.status || "")
    .toLowerCase()
    .trim()
    .replace(/[-\s]+/g, "_")
  if (s === "en_route" || s === "enroute") return "en_route"
  if (
    s === "in_progress" ||
    s === "started" ||
    s === "onsite" ||
    s === "on_site"
  ) {
    return "in_progress"
  }
  if (s === "completed" || s === "complete" || s === "done" || s === "finished") {
    return "completed"
  }
  if (s === "cancelled" || s === "canceled") return "cancelled"
  return "scheduled"
}

const JOB_STATUS_SIGN = {
  en_route: { label: "On the way", color: "#2563EB", Icon: Navigation },
  in_progress: { label: "Started", color: "#D97706", Icon: CircleDot },
  completed: { label: "Finished", color: "#16A34A", Icon: Check },
  scheduled: { label: "Scheduled", color: "#94A3B8", Icon: Moon },
}

const jobStatusSign = (j) => {
  const key = normalizeJobStatus(j)
  return JOB_STATUS_SIGN[key] || JOB_STATUS_SIGN.scheduled
}

// Backend has historically returned is_recurring as bool/int/string across
// different code paths — tolerate all truthy variants.
const isRecurringJob = (j) => {
  const v = j?.is_recurring
  return v === true || v === "true" || v === 1 || v === "1" || !!j?.recurring_frequency
}

// New-job window — a job created (or duplicated) within the last 24 hours
// gets a "NEW" badge on the schedule chips (#52). Covers duplicates too
// because duplication inserts a fresh row with a current created_at.
const NEW_JOB_WINDOW_MS = 24 * 60 * 60 * 1000
const isNewJob = (j) => {
  const src = j?.created_at
  if (!src) return false
  const t = new Date(src).getTime()
  if (!Number.isFinite(t)) return false
  return Date.now() - t <= NEW_JOB_WINDOW_MS
}

const NewBadge = () => (
  <span
    title="Created in the last 24 hours"
    aria-label="New job"
    style={{
      display: "inline-flex",
      alignItems: "center",
      height: 12,
      padding: "0 4px",
      background: "var(--sf-amber, #d97706)",
      color: "#fff",
      fontSize: 8.5,
      fontWeight: 800,
      letterSpacing: "0.04em",
      borderRadius: 2,
      flexShrink: 0,
      fontFamily: "var(--sf-font-ui)",
    }}
  >
    NEW
  </span>
)

// Small circular ↻ chip rendered beside the status sign on month + week
// chips so recurring jobs are identifiable at a glance (issue #51 — the
// badge existed in the pre-v2 schedule, was dropped in the v2 rewrite).
const RecurringChip = ({ size = 12 }) => (
  <span
    title="Recurring job"
    aria-label="Recurring job"
    style={{
      width: size,
      height: size,
      borderRadius: "50%",
      background: "var(--sf-blue-500, #2563eb)",
      color: "#fff",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
    }}
  >
    <RotateCw size={Math.max(7, Math.round(size * 0.6))} strokeWidth={2.75} />
  </span>
)

const StatusSign = ({ job, size = 14 }) => {
  const sign = jobStatusSign(job)
  if (normalizeJobStatus(job) === "cancelled") return null
  const Icon = sign.Icon
  const iconPx = Math.max(8, Math.round(size * 0.58))
  return (
    <span
      title={sign.label}
      aria-label={sign.label}
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        background: sign.color,
        color: "#fff",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      <Icon size={iconPx} strokeWidth={2.5} />
    </span>
  )
}

const durationMinutes = (job) => {
  const raw = job.duration || job.service_duration || job.estimated_duration || 60
  const n = parseInt(raw, 10)
  return Number.isFinite(n) && n > 0 ? n : 60
}

// Each job is a card anchored to its start time on the vertical grid, with
// height = duration (so a 2p-7p job visibly spans 2p-7p). If a card's top
// would collide with the prior card's header strip, the later card cascades
// down by the header height so its time/name stays readable — "one over
// another, leaving place for the info". Returns top/height in px.
// Header carries time · duration · customer on line 1 and cleaner names on
// line 2 — both always visible even when overlapping blocks cascade on top
// of each other. Bump this if you change the header's line count.
const HEADER_HEIGHT_PX = 34
const HEADER_GAP_PX = 1

const layoutDay = (dayJobs, startHr, endHr, colHeightPx) => {
  const totalMins = (endHr - startHr) * 60
  const sorted = [...dayJobs]
    .map((j) => {
      const d = jobStartDateTime(j)
      if (!d) return null
      const start = d.getHours() * 60 + d.getMinutes()
      const dur = Math.max(durationMinutes(j), 15)
      return { job: j, start, dur }
    })
    .filter(Boolean)
    .filter((x) => x.start >= startHr * 60 && x.start <= endHr * 60)
    .sort((a, b) => a.start - b.start)

  let lastHeaderBottom = -Infinity
  return sorted.map((x) => {
    const basePos = ((x.start - startHr * 60) / totalMins) * colHeightPx
    const baseHeight = Math.max((x.dur / totalMins) * colHeightPx, HEADER_HEIGHT_PX)
    const top = Math.max(basePos, lastHeaderBottom + HEADER_GAP_PX)
    lastHeaderBottom = top + HEADER_HEIGHT_PX
    return { job: x.job, start: x.start, top, height: baseHeight }
  })
}

const sameDay = (a, b) =>
  a && b &&
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate()

const startOfDay = (d) => {
  const x = new Date(d)
  x.setHours(0, 0, 0, 0)
  return x
}

const addDays = (d, n) => {
  const x = new Date(d)
  x.setDate(x.getDate() + n)
  return x
}

const startOfWeek = (d) => {
  // Monday-start week (matches the prototype)
  const x = startOfDay(d)
  const dow = (x.getDay() + 6) % 7 // Mon=0 .. Sun=6
  return addDays(x, -dow)
}

const formatHourLabel = (h) => {
  if (h === 0) return "12a"
  if (h === 12) return "12p"
  return h < 12 ? `${h}a` : `${h - 12}p`
}

const formatJobTime = (job) => {
  const d = jobStartDateTime(job)
  if (!d) return ""
  return d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }).replace(":00", "")
}

// "10a–2:30p" — start + start+duration end. Compact so it fits inside the
// narrow time column of ShiftJobRow / modal listings. minsToLabel is
// hoisted so it's safe to call at render time.
const formatJobRange = (job) => {
  const d = jobStartDateTime(job)
  if (!d) return ""
  const startMin = d.getHours() * 60 + d.getMinutes()
  const endMin = startMin + durationMinutes(job)
  return `${minsToLabel(startMin)}–${minsToLabel(endMin % (24 * 60))}`
}

const formatRangeLabel = (view, anchor) => {
  if (view === "day") {
    return anchor.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" })
  }
  if (view === "week") {
    const start = startOfWeek(anchor)
    const end = addDays(start, 6)
    const sameMonth = start.getMonth() === end.getMonth()
    if (sameMonth) {
      return `${start.toLocaleDateString("en-US", { month: "short", day: "numeric" })} – ${end.getDate()}, ${end.getFullYear()}`
    }
    return `${start.toLocaleDateString("en-US", { month: "short", day: "numeric" })} – ${end.toLocaleDateString("en-US", { month: "short", day: "numeric" })}, ${end.getFullYear()}`
  }
  return anchor.toLocaleDateString("en-US", { month: "long", year: "numeric" })
}

// ── Page ────────────────────────────────────────────────────

const TABS = [
  { id: "schedule",   label: "Schedule" },
  { id: "availability", label: "Availability" },
  { id: "routes",     label: "Routes" },
  { id: "unassigned", label: "Unassigned" },
]

const VIEWS = [
  { id: "day",   label: "Day" },
  { id: "week",  label: "Week" },
  { id: "month", label: "Month" },
]

const ScheduleV2 = () => {
  const { user } = useAuth()
  const { locationId } = useLocationScope()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [searchParams, setSearchParams] = useSearchParams()
  const isMobile = useIsMobile()

  const initialTab = TABS.find((t) => t.id === searchParams.get("tab"))?.id || "schedule"
  const initialView = VIEWS.find((v) => v.id === searchParams.get("view"))?.id || "week"

  const [tab, setTab] = useState(initialTab)
  const [view, setView] = useState(initialView)
  const [anchor, setAnchor] = useState(() => new Date())
  const [selectedTeams, setSelectedTeams] = useState(null) // null = all
  // Availability tab: territory scope (null = all territories)
  const [availabilityTerritoryId, setAvailabilityTerritoryId] = useState(null)
  // Manage-shift modal — opened from any Availability cell or daily
  // tile. Shape: { teamId, dayIdx?, jobId?, openSlot? } or null.
  const [manageShift, setManageShift] = useState(null)
  // Company travel buffer between jobs (Settings → Availability → driving time)
  const [companyDrivingMinutes, setCompanyDrivingMinutes] = useState(0)
  useEffect(() => {
    if (!user?.id) return
    let cancelled = false
    availabilityAPI.getAvailability(user.id).then((data) => {
      let bh = data?.businessHours || data?.business_hours
      if (typeof bh === "string") {
        try { bh = JSON.parse(bh) } catch { bh = null }
      }
      const v = parseInt(bh?.drivingTime, 10)
      if (!cancelled) setCompanyDrivingMinutes(Number.isFinite(v) ? Math.max(0, v) : 0)
    }).catch(() => {})
    return () => { cancelled = true }
  }, [user?.id])

  // URL sync
  useEffect(() => {
    setSearchParams((sp) => {
      const next = new URLSearchParams(sp)
      next.set("tab", tab)
      if (tab === "schedule" || tab === "availability") next.set("view", view)
      else next.delete("view")
      return next
    }, { replace: true })
  }, [tab, view, setSearchParams])

  // Constrain job fetch to a ~5-month window centered on the visible
  // anchor, quantized to month boundaries so nudging within the window
  // hits the cache instead of refetching. Old behavior pulled every job
  // ever created (up to 10k rows sequentially) on every mount.
  const fetchWindow = useMemo(() => {
    const a = anchor || new Date()
    const start = new Date(a.getFullYear(), a.getMonth() - 2, 1)
    const end = new Date(a.getFullYear(), a.getMonth() + 3, 0)
    const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`
    return { start: fmt(start), end: fmt(end) }
  }, [anchor])

  const jobsQuery = useQuery({
    queryKey: ["schedule-jobs", user?.id, fetchWindow.start, fetchWindow.end],
    enabled: !!user?.id,
    queryFn: async () => {
      const range = `${fetchWindow.start} to ${fetchWindow.end}`
      const resp = await jobsAPI.getAll(
        user.id, "", "", 1, 1000, null, range, "scheduled_date", "ASC",
        null, null, null, null, null, null, { noCount: true }
      )
      const list = normalizeAPIResponse(resp, "jobs") || []
      return list.filter((j) => !isCancelledJob(j))
    },
  })

  const teamQuery = useQuery({
    queryKey: ["schedule-team", user?.id],
    enabled: !!user?.id,
    queryFn: async () => {
      const tmResp = await teamAPI.getAll(user.id, { page: 1, limit: 500 })
      const list = tmResp?.teamMembers || tmResp?.members || (Array.isArray(tmResp) ? tmResp : [])
      return Array.isArray(list) ? list : []
    },
  })

  const territoriesQuery = useQuery({
    queryKey: ["schedule-territories", user?.id],
    enabled: !!user?.id,
    queryFn: async () => {
      const tResp = await territoriesAPI.getAll(user.id, { limit: 200 })
      const list = tResp?.territories || (Array.isArray(tResp) ? tResp : [])
      return Array.isArray(list) ? list : []
    },
  })

  const jobs = jobsQuery.data || []
  const teamMembers = teamQuery.data || []
  const territories = territoriesQuery.data || []
  const loading = jobsQuery.isLoading || teamQuery.isLoading || territoriesQuery.isLoading

  // Callback for child modals that mutate jobs. Invalidates every window
  // of the schedule-jobs cache so the next render pulls fresh rows.
  const fetchData = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["schedule-jobs", user?.id] })
    queryClient.invalidateQueries({ queryKey: ["schedule-team", user?.id] })
  }, [queryClient, user?.id])

  // Location filter + worker scope. Workers (team members) only see jobs
  // they're actually assigned to — everything else on this page (team
  // selector, territories, availability grid) is dormant for them because
  // the underlying job list is empty of others' work.
  const isWorkerUser = isWorker(user)
  const myTeamMemberId = user?.teamMemberId ? String(user.teamMemberId) : null
  const scopedJobs = useMemo(() => {
    const byLoc = filterByLocation(jobs, locationId)
    if (!isWorkerUser || !myTeamMemberId) return byLoc
    return byLoc.filter((j) => assigneesFor(j).some((a) => String(a.id) === myTeamMemberId))
  }, [jobs, locationId, isWorkerUser, myTeamMemberId])

  // Build cleaner color map from the whole job set so colors stay
  // stable across day/week/month views
  const allCleanerIds = useMemo(() => {
    const set = new Set()
    scopedJobs.forEach((j) => assigneesFor(j).forEach((a) => set.add(a.id)))
    return Array.from(set)
  }, [scopedJobs])

  const colorMap = useMemo(() => sfAssignTeamColors(allCleanerIds), [allCleanerIds])
  const cleanerColor = useCallback(
    (id) => (id == null ? "#DC2626" : (colorMap.get(String(id)) || sfTeamColor(0))),
    [colorMap]
  )

  // id → name lookup
  const cleanerNameById = useMemo(() => {
    const map = new Map()
    teamMembers.forEach((m) => {
      const id = m?.id
      if (id == null) return
      const name =
        m.name ||
        `${m.first_name || ""} ${m.last_name || ""}`.trim() ||
        m.email ||
        ""
      if (name) map.set(String(id), name)
    })
    return map
  }, [teamMembers])

  const resolveCleanerName = useCallback(
    (id, fallback) => {
      if (!id) return fallback || ""
      return cleanerNameById.get(String(id)) || fallback || ""
    },
    [cleanerNameById]
  )

  // Team-chip selection. `null` = all cleaners shown. Clicking a chip
  // solos that cleaner; clicking the same chip again clears back to all.
  const isCleanerSelected = useCallback(
    (id) => selectedTeams === null || selectedTeams.has(String(id)),
    [selectedTeams]
  )

  const toggleCleaner = (id) => {
    setSelectedTeams((prev) => {
      const key = String(id)
      if (prev && prev.size === 1 && prev.has(key)) return null
      return new Set([key])
    })
  }

  // Apply chip filter — a job survives if at least one of its
  // assignees is selected, or if it's unassigned (always shown).
  const teamFilteredJobs = useMemo(() => {
    if (selectedTeams === null) return scopedJobs
    return scopedJobs.filter((j) => {
      const assignees = assigneesFor(j)
      if (assignees.length === 0) return true
      return assignees.some((a) => selectedTeams.has(String(a.id)))
    })
  }, [scopedJobs, selectedTeams])

  // Search box (issue #40). Matches on customer name, email, phone,
  // address, job id, and service name — same shape jobs-v2 uses, plus
  // email/phone which goscha explicitly called out. 200ms debounce
  // keeps the big weekly grid responsive while typing.
  const [searchQuery, setSearchQuery] = useState("")
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("")
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearchQuery(searchQuery.trim()), 200)
    return () => clearTimeout(t)
  }, [searchQuery])

  const searchedJobs = useMemo(() => {
    const q = debouncedSearchQuery.toLowerCase()
    if (!q) return teamFilteredJobs
    return teamFilteredJobs.filter((j) => {
      const name = `${j.customer_first_name || ""} ${j.customer_last_name || ""} ${j.customer_name || ""} ${j.customer?.first_name || ""} ${j.customer?.last_name || ""}`.toLowerCase()
      const email = String(j.customer_email || j.customer?.email || "").toLowerCase()
      const phoneRaw = String(j.customer_phone || j.customer?.phone || "").toLowerCase()
      const phoneDigits = phoneRaw.replace(/\D+/g, "")
      const qDigits = q.replace(/\D+/g, "")
      const addr = [
        j.service_address, j.customer_address,
        j.service_address_street, j.service_address_city, j.service_address_state, j.service_address_zip,
      ].filter(Boolean).join(" ").toLowerCase()
      const id = String(j.id || "").toLowerCase()
      const svc = String(j.service_name || "").toLowerCase()
      if (name.includes(q)) return true
      if (email && email.includes(q)) return true
      if (phoneRaw.includes(q)) return true
      if (qDigits && phoneDigits && phoneDigits.includes(qDigits)) return true
      if (addr.includes(q)) return true
      if (id === q || id.endsWith(q)) return true
      if (svc.includes(q)) return true
      return false
    })
  }, [teamFilteredJobs, debouncedSearchQuery])

  // Availability tab shows only active cleaners — inactive / on_leave /
  // soft-deleted members shouldn't appear on the capacity grid. Same
  // predicate the Create Job team picker uses. Also filters by the
  // Availability-tab territory dropdown when one is picked.
  //
  // Managers / account owners stay visible for reference (their shift
  // schedule is often the "office is open" signal operators want to see
  // at a glance) but are sorted below bookable cleaners and marked as
  // informative — they don't feed KPI capacity or anything sent to LB /
  // any downstream availability consumer. See isBookableCleaner below.
  const activeCleaners = useMemo(() => {
    const rows = (teamMembers || []).filter((m) => {
      const status = String(m?.status || '').toLowerCase()
      if (status === 'inactive' || status === 'on_leave') return false
      if (m?.is_active === false) return false
      if (m?.active === false) return false
      if (m?.deleted_at) return false
      if (availabilityTerritoryId != null) {
        let terrs = m?.territories
        if (typeof terrs === 'string') {
          try { terrs = JSON.parse(terrs) } catch { terrs = [] }
        }
        if (!Array.isArray(terrs)) return false
        const idNum = Number(availabilityTerritoryId)
        if (!terrs.map(Number).filter(Number.isFinite).includes(idNum)) return false
      }
      return true
    })
    // Bookable cleaners first, managers/schedulers below. Preserve the
    // original relative order within each group so operator-set sort
    // (e.g. name / territory) isn't clobbered — we're only bucketing.
    const bookable = []
    const informative = []
    for (const m of rows) (isBookableTeamMember(m) ? bookable : informative).push(m)
    return [...bookable, ...informative]
  }, [teamMembers, availabilityTerritoryId])

  // Date nav
  const nudgeAnchor = (dir) => {
    setAnchor((prev) => {
      if (view === "day") return addDays(prev, dir)
      if (view === "week") return addDays(prev, dir * 7)
      const x = new Date(prev)
      x.setMonth(x.getMonth() + dir)
      return x
    })
  }

  const unassignedCount = useMemo(
    () => scopedJobs.filter((j) => !isCancelledJob(j) && assigneesFor(j).length === 0).length,
    [scopedJobs]
  )

  // Cleaners with a job in the currently-visible range (day/week/month).
  // Drives the toolbar chip row so only actually-scheduled cleaners
  // appear — the full-team roster stays in `allCleanerIds` so the
  // color map remains stable across views.
  const visibleRange = useMemo(() => {
    if (view === "day") {
      const start = startOfDay(anchor)
      return { start, end: addDays(start, 1) }
    }
    if (view === "week") {
      const start = startOfWeek(anchor)
      return { start, end: addDays(start, 7) }
    }
    const start = new Date(anchor.getFullYear(), anchor.getMonth(), 1)
    const end = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 1)
    return { start, end }
  }, [view, anchor])

  const rangeCleanerIds = useMemo(() => {
    const set = new Set()
    scopedJobs.forEach((j) => {
      const d = jobStartDateTime(j)
      if (!d || d < visibleRange.start || d >= visibleRange.end) return
      assigneesFor(j).forEach((a) => set.add(a.id))
    })
    return Array.from(set)
  }, [scopedJobs, visibleRange])

  const onJobClick = useCallback(
    (job) => navigate(`/job/${job.id}`),
    [navigate]
  )

  // ── Context menu (issue #36) ──────────────────────────────
  // Right-click a job chip to pop a floating menu with Open /
  // Set reminders / Create task / status transitions / Cancel.
  // Keeps the entire feature local to this page — each view only
  // receives one new prop (onJobContextMenu).
  const [ctxMenu, setCtxMenu] = useState(null) // { job, x, y } | null
  const [taskModalJob, setTaskModalJob] = useState(null) // job | null
  const [taskLeads, setTaskLeads] = useState([])
  const [taskSaving, setTaskSaving] = useState(false)
  // Duplicate-job modal state (issue #38)
  const [duplicateModalJob, setDuplicateModalJob] = useState(null)
  const [duplicateBusy, setDuplicateBusy] = useState(false)
  // Convert-to-recurring modal state (issue #39)
  const [recurringModalJob, setRecurringModalJob] = useState(null)
  const [recurringBusy, setRecurringBusy] = useState(false)

  const openJobContextMenu = useCallback((job, event) => {
    if (event?.preventDefault) event.preventDefault()
    if (event?.stopPropagation) event.stopPropagation()
    const x = event?.clientX ?? 0
    const y = event?.clientY ?? 0
    setCtxMenu({ job, x, y })
  }, [])

  const closeCtxMenu = useCallback(() => setCtxMenu(null), [])

  const onSetReminders = useCallback(
    () => navigate("/settings/client-team-notifications/appointment-reminder"),
    [navigate]
  )

  const onCreateTaskForJob = useCallback(async (job) => {
    // Lazy-load the user's leads so the task modal's lead picker is
    // populated. Fire-and-forget — the modal opens immediately.
    try {
      const data = await leadsAPI.getAll()
      const list = Array.isArray(data) ? data : (data?.leads || data?.data || [])
      setTaskLeads(list)
    } catch (e) {
      console.warn("[schedule] failed to load leads for task modal:", e?.message)
    }
    setTaskModalJob(job)
  }, [])

  const onSubmitTask = useCallback(async (taskData) => {
    if (!taskModalJob) return
    // The modal sends { leadId } (NOT selectedLeadId — that's the modal's
    // internal state key). Tasks are lead-scoped in the current backend;
    // without a leadId there's nowhere to write.
    const leadId = taskData?.leadId
    if (!leadId) {
      alert('Pick a lead to attach this task to. Tasks are grouped by lead today — a per-job tasks table is coming later.')
      return
    }
    setTaskSaving(true)
    try {
      await leadsAPI.createTask(leadId, {
        ...taskData,
        // Tag with the originating job so operators can trace it back
        // from the Tasks page. Harmless if the backend ignores it.
        source_job_id: taskModalJob.id,
      })
      setTaskModalJob(null)
    } catch (e) {
      alert(e?.response?.data?.error || e?.message || 'Could not create task.')
    } finally {
      setTaskSaving(false)
    }
  }, [taskModalJob])

  const onCtxSetStatus = useCallback(async (job, newStatus) => {
    try {
      await jobsAPI.updateStatus(job.id, newStatus)
      // Match the existing invalidation key used elsewhere in this file
      // so the chip updates its color / status pill immediately.
      queryClient.invalidateQueries({ queryKey: ["schedule-jobs", user?.id] })
    } catch (e) {
      alert(e?.response?.data?.error || e?.message || "Could not update status.")
    }
  }, [queryClient, user?.id])

  // Open the duplicate modal for the given job. Backend copies all
  // fields and swaps in scheduledDate/scheduledTime (issue #38).
  const onCtxDuplicate = useCallback((job) => {
    setDuplicateModalJob(job)
  }, [])

  const onConfirmDuplicate = useCallback(async (payload) => {
    if (!duplicateModalJob) return
    const { scheduledDate, scheduledTime, customerId, serviceId, serviceName, price, duration } = payload
    setDuplicateBusy(true)
    try {
      const result = await jobsAPI.duplicate(duplicateModalJob.id, { scheduledDate, scheduledTime, customerId, serviceId, serviceName, price, duration })
      const newId = result?.id || result?.job?.id || result?.jobId
      // Defensive: if the backend duplicate endpoint ignores customer /
      // service overrides, patch the newly-created job directly so the
      // override still lands. Overrides must affect ONLY the duplicate
      // (issue #38 reopen x4) — the source job is never touched.
      if (newId && (customerId || serviceId)) {
        const patch = {}
        if (customerId) patch.customer_id = customerId
        if (serviceId) patch.service_id = serviceId
        if (serviceName) patch.service_name = serviceName
        if (price != null && price !== "") patch.price = Number(price)
        if (duration != null && duration !== "") patch.service_duration = Number(duration)
        try { await jobsAPI.update(newId, patch) } catch { /* non-fatal; backend may already have applied it */ }
      }
      setDuplicateModalJob(null)
      queryClient.invalidateQueries({ queryKey: ["schedule-jobs", user?.id] })
      if (newId) navigate(`/job/${newId}`)
    } catch (e) {
      // DuplicateJobModal surfaces the error inline.
      throw e
    } finally {
      setDuplicateBusy(false)
    }
  }, [duplicateModalJob, queryClient, user?.id, navigate])

  // Convert-to-recurring (#39). Opens a modal; on confirm, duplicates
  // the parent job as a recurring series with optional service/price
  // overrides. The original one-time job is left untouched per spec.
  const onCtxConvertRecurring = useCallback((job) => {
    setRecurringModalJob(job)
  }, [])

  const onConfirmConvertRecurring = useCallback(async (payload) => {
    if (!recurringModalJob) return
    setRecurringBusy(true)
    try {
      const result = await jobsAPI.duplicate(recurringModalJob.id, payload)
      setRecurringModalJob(null)
      queryClient.invalidateQueries({ queryKey: ["schedule-jobs", user?.id] })
      const newId = result?.id || result?.job?.id || result?.jobId
      if (newId) navigate(`/job/${newId}`)
    } catch (e) {
      throw e // ConvertToRecurringModal surfaces inline
    } finally {
      setRecurringBusy(false)
    }
  }, [recurringModalJob, queryClient, user?.id, navigate])

  const onCtxCancelJob = useCallback(async (job) => {
    const reason = window.prompt("Reason for cancellation (optional):")
    if (reason === null) return // user hit Cancel on the prompt
    try {
      await jobsAPI.cancel(job.id, { reason })
      queryClient.invalidateQueries({ queryKey: ["schedule-jobs", user?.id] })
    } catch (e) {
      alert(e?.response?.data?.error || e?.message || "Could not cancel the job.")
    }
  }, [queryClient, user?.id])

  return (
    <div className="min-h-screen bg-[var(--sf-bg-page)] flex flex-col" style={{ fontFamily: "var(--sf-font-ui)" }}>
      <MobileHeader title="Schedule" />

      <SfPageHeader
        eyebrow="Operations"
        title="Schedule"
        subtitle={
          tab === "schedule"
            ? formatRangeLabel(view, anchor)
            : tab === "availability"
            ? "Team availability · this week"
            : tab === "routes"
            ? "Live routes (coming soon)"
            : `${unassignedCount} unassigned job${unassignedCount === 1 ? "" : "s"}`
        }
        actions={
          <>
            {/* Issue #40 — search by customer name / job id / email /
                phone / address. Debounced in useMemo; shows on sm+ to
                leave room for the view / date controls on phones. */}
            {tab === "schedule" && (
              <div className="hidden sm:flex items-center bg-white border border-[var(--sf-border-light,#e2e8f0)] rounded-md pl-2 pr-1 h-9 w-[280px] focus-within:ring-2 focus-within:ring-[var(--sf-blue-500)]/40 focus-within:border-[var(--sf-blue-500)]">
                <SearchIcon size={14} className="text-[var(--sf-ink-3,#94a3b8)] shrink-0" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search jobs by name, id, email, phone, address…"
                  className="flex-1 min-w-0 px-2 text-[12.5px] bg-transparent border-none outline-none placeholder:text-[var(--sf-ink-3,#94a3b8)]"
                  style={{ fontFamily: "var(--sf-font-ui)" }}
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery("")}
                    className="text-[var(--sf-ink-3,#94a3b8)] hover:text-[var(--sf-ink,#0f172a)] text-xs px-1.5"
                    aria-label="Clear search"
                  >
                    ✕
                  </button>
                )}
              </div>
            )}
            <SfButton variant="secondary" size="md" icon={Filter} className="hidden sm:inline-flex">
              Filters
            </SfButton>
            <SfButton variant="secondary" size="md" icon={Download} className="hidden sm:inline-flex">
              Export
            </SfButton>
            <SfButton
              variant="primary"
              size="md"
              icon={Plus}
              onClick={() => navigate("/createjob")}
            >
              New job
            </SfButton>
          </>
        }
        tabs={
          <div className="flex items-center overflow-x-auto scrollbar-hide w-full">
            {TABS.map((t) => (
              <SfTab
                key={t.id}
                active={tab === t.id}
                count={t.id === "unassigned" ? unassignedCount : undefined}
                onClick={() => setTab(t.id)}
              >
                {t.label}
              </SfTab>
            ))}
          </div>
        }
      />

      {tab === "schedule" && (
        <>
          <ScheduleToolbar
            view={view}
            setView={setView}
            anchor={anchor}
            setAnchor={setAnchor}
            nudge={nudgeAnchor}
            cleaners={rangeCleanerIds}
            cleanerColor={cleanerColor}
            resolveName={resolveCleanerName}
            isSelected={isCleanerSelected}
            toggleCleaner={toggleCleaner}
          />
          <div className="px-4 sm:px-6 lg:px-8 pb-8 flex-1">
            {loading ? (
              <SfCard>
                <div className="py-16 text-center text-[12.5px] text-[var(--sf-ink-3)]">
                  Loading schedule…
                </div>
              </SfCard>
            ) : view === "day" ? (
              <DayView
                anchor={anchor}
                jobs={searchedJobs}
                cleanerColor={cleanerColor}
                resolveName={resolveCleanerName}
                onJobClick={onJobClick}
                onJobContextMenu={openJobContextMenu}
              />
            ) : view === "week" ? (
              isMobile ? (
                <WeekViewMobile
                  anchor={anchor}
                  jobs={searchedJobs}
                  onPickDay={(d) => { setAnchor(d); setView("day") }}
                />
              ) : (
                <WeekView
                  anchor={anchor}
                  jobs={searchedJobs}
                  cleanerColor={cleanerColor}
                  resolveName={resolveCleanerName}
                  onJobClick={onJobClick}
                  onJobContextMenu={openJobContextMenu}
                />
              )
            ) : (
              <MonthView
                anchor={anchor}
                jobs={searchedJobs}
                cleanerColor={cleanerColor}
                resolveName={resolveCleanerName}
                onJobClick={onJobClick}
                onJobContextMenu={openJobContextMenu}
                onPickDay={(d) => { setAnchor(d); setView("day") }}
              />
            )}
          </div>
        </>
      )}

      {tab === "availability" && (
        <>
          <ScheduleToolbar
            view={view}
            setView={setView}
            anchor={anchor}
            setAnchor={setAnchor}
            nudge={nudgeAnchor}
            cleaners={activeCleaners.map((c) => c.id)}
            cleanerColor={cleanerColor}
            resolveName={resolveCleanerName}
            isSelected={isCleanerSelected}
            toggleCleaner={toggleCleaner}
          />
          <div className="px-4 sm:px-6 lg:px-8 pb-8 flex-1">
            <AvailabilityView
              view={view}
              setView={setView}
              setAnchor={setAnchor}
              cleaners={
                selectedTeams === null
                  ? activeCleaners
                  : activeCleaners.filter((c) => selectedTeams.has(String(c.id)))
              }
              jobs={searchedJobs}
              cleanerColor={cleanerColor}
              resolveName={resolveCleanerName}
              anchor={anchor}
              onOpenManageShift={(payload) => setManageShift(payload)}
              territories={territories}
              selectedTerritoryId={availabilityTerritoryId}
              onSelectTerritory={setAvailabilityTerritoryId}
              onSyncFromZB={fetchData}
              companyDrivingMinutes={companyDrivingMinutes}
            />
          </div>
        </>
      )}

      {tab === "routes" && (
        <div className="px-4 sm:px-6 lg:px-8 pb-8 pt-3 flex-1">
          <SfCard>
            <div className="py-12 flex flex-col items-center text-center">
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center mb-3"
                style={{ background: "var(--sf-blue-soft)", color: "var(--sf-blue-dark)" }}
              >
                <MapIcon size={22} />
              </div>
              <div className="text-[14px] font-semibold text-[var(--sf-ink)]">Routes view</div>
              <div className="text-[12.5px] text-[var(--sf-ink-2)] mt-1 max-w-md">
                Live route map with team locations and optimized routing — coming next slice.
              </div>
            </div>
          </SfCard>
        </div>
      )}

      {tab === "unassigned" && (
        <div className="px-4 sm:px-6 lg:px-8 pb-8 pt-3 flex-1">
          <UnassignedView
            jobs={scopedJobs}
            onJobClick={onJobClick}
            onAssign={(j) => navigate(`/job/${j.id}`)}
          />
        </div>
      )}

      <ManageShiftModal
        open={!!manageShift}
        payload={manageShift}
        onClose={() => setManageShift(null)}
        jobs={scopedJobs}
        cleaners={teamMembers}
        cleanerColor={cleanerColor}
        resolveName={resolveCleanerName}
        anchor={anchor}
        onMutated={fetchData}
        onOpenJob={onJobClick}
        onNewJob={(teamId, slot) => {
          const params = new URLSearchParams()
          if (teamId) params.set("teamMemberId", String(teamId))
          if (slot?.day instanceof Date) params.set("scheduledDate", formatDateKey(slot.day))
          if (slot?.start != null) {
            const hh = String(Math.floor(slot.start / 60)).padStart(2, "0")
            const mm = String(slot.start % 60).padStart(2, "0")
            params.set("scheduledTime", `${hh}:${mm}`)
          }
          const qs = params.toString()
          navigate(qs ? `/createjob?${qs}` : "/createjob")
        }}
      />

      {/* Right-click context menu on job chips (issue #36). */}
      <JobContextMenu
        state={ctxMenu}
        onClose={closeCtxMenu}
        onOpenJob={onJobClick}
        onSetReminders={onSetReminders}
        onCreateTask={onCreateTaskForJob}
        onDuplicate={onCtxDuplicate}
        onConvertRecurring={onCtxConvertRecurring}
        onSetStatus={onCtxSetStatus}
        onCancelJob={onCtxCancelJob}
      />

      {/* Duplicate-job modal — issue #38. User only picks the new date;
          backend copies every other field from the parent. */}
      <DuplicateJobModal
        open={!!duplicateModalJob}
        job={duplicateModalJob}
        defaultDate={duplicateModalJob?.scheduled_date}
        defaultTime={duplicateModalJob?.scheduled_time}
        busy={duplicateBusy}
        onClose={() => setDuplicateModalJob(null)}
        onConfirm={onConfirmDuplicate}
      />

      {/* Convert-to-recurring modal — issue #39. Duplicates the parent
          as a recurring series with optional service/price overrides. */}
      <ConvertToRecurringModal
        open={!!recurringModalJob}
        job={recurringModalJob}
        defaultDate={recurringModalJob?.scheduled_date}
        defaultTime={recurringModalJob?.scheduled_time}
        busy={recurringBusy}
        onClose={() => setRecurringModalJob(null)}
        onConfirm={onConfirmConvertRecurring}
      />

      {/* Task creation launched from the context menu. */}
      <CreateTaskModal
        isOpen={!!taskModalJob}
        onClose={() => setTaskModalJob(null)}
        onSubmit={onSubmitTask}
        leads={taskLeads}
        teamMembers={teamMembers}
        initialDate={taskModalJob?.scheduled_date || null}
        leadId={null}
        isEditing={false}
      />
      {taskSaving && (
        <div className="fixed bottom-6 right-6 bg-slate-900 text-white text-sm px-3 py-2 rounded-md shadow-lg z-[1001]">
          Creating task…
        </div>
      )}
    </div>
  )
}

// ── Toolbar ────────────────────────────────────────────────

// Date range pill + Today button. Shared between the Schedule and
// Availability toolbars so the calendar-switcher UX stays identical
// across tabs. `view` controls both the label shape (day vs week vs
// month) and the nudge stride.
const DateNavigator = ({ view, anchor, setAnchor, nudge }) => (
  <div className="flex items-center gap-2 flex-shrink-0">
    <div
      className="flex items-center bg-[var(--sf-panel)] border border-[var(--sf-border-soft)] rounded-md"
      style={{ boxShadow: "var(--sf-shadow)" }}
    >
      <button
        onClick={() => nudge(-1)}
        aria-label="Previous"
        className="px-2 py-1.5 text-[var(--sf-ink-2)] hover:text-[var(--sf-ink)]"
        style={{ background: "transparent", border: "none", borderRight: "1px solid var(--sf-border-soft)", cursor: "pointer" }}
      >
        <ChevronLeft size={14} />
      </button>
      <span
        className="px-3 py-1.5 text-[12.5px] font-semibold text-[var(--sf-ink)]"
        style={{ fontVariantNumeric: "tabular-nums" }}
      >
        {formatRangeLabel(view, anchor)}
      </span>
      <button
        onClick={() => nudge(1)}
        aria-label="Next"
        className="px-2 py-1.5 text-[var(--sf-ink-2)] hover:text-[var(--sf-ink)]"
        style={{ background: "transparent", border: "none", borderLeft: "1px solid var(--sf-border-soft)", cursor: "pointer" }}
      >
        <ChevronRight size={14} />
      </button>
    </div>
    <SfButton variant="secondary" size="sm" onClick={() => setAnchor(new Date())}>
      Today
    </SfButton>
  </div>
)

// Day/Week/Month pill group — reused by both Schedule and Availability
// toolbars so the switcher UI is identical across tabs.
const ViewSwitcher = ({ view, setView }) => (
  <div
    className="flex bg-[var(--sf-panel-soft)] rounded-md flex-shrink-0"
    style={{ padding: 2 }}
  >
    {VIEWS.map((v) => (
      <button
        key={v.id}
        onClick={() => setView(v.id)}
        style={{
          padding: "4px 11px",
          fontSize: 11.5,
          fontWeight: 600,
          background: view === v.id ? "var(--sf-panel)" : "transparent",
          color: view === v.id ? "var(--sf-ink)" : "var(--sf-ink-2)",
          border: "none",
          borderRadius: 5,
          cursor: "pointer",
          fontFamily: "var(--sf-font-ui)",
          boxShadow: view === v.id ? "0 1px 2px rgba(15,23,42,.08)" : "none",
        }}
      >
        {v.label}
      </button>
    ))}
  </div>
)

// Cleaner filter dropdown — replaces the old horizontal chip row that
// overflowed when there were more than ~8 cleaners. Opens a scrollable
// panel; selected cleaner shows at full opacity, the rest are dimmed so
// a glance tells you which cleaner you're soloed on.
const CleanerDropdown = ({ cleaners, cleanerColor, resolveName, isSelected, toggleCleaner }) => {
  const [open, setOpen] = useState(false)
  const rootRef = useRef(null)

  useEffect(() => {
    if (!open) return
    const onDocDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false)
    }
    const onKey = (e) => { if (e.key === "Escape") setOpen(false) }
    document.addEventListener("mousedown", onDocDown)
    document.addEventListener("keydown", onKey)
    return () => {
      document.removeEventListener("mousedown", onDocDown)
      document.removeEventListener("keydown", onKey)
    }
  }, [open])

  // When `isSelected` returns true for every cleaner (= "null" state in
  // parent), the filter is in "show all" mode. If it returns true for
  // only some, exactly one cleaner is soloed.
  const soloId = useMemo(() => {
    const picked = cleaners.filter((id) => isSelected(id))
    if (picked.length === cleaners.length || picked.length === 0) return null
    return picked.length === 1 ? picked[0] : null
  }, [cleaners, isSelected])

  const soloName = soloId != null ? resolveName(soloId, "") : ""
  const soloColor = soloId != null ? cleanerColor(soloId) : null
  const soloInitials = soloId != null ? (sfInitials(soloName) || "?") : null

  return (
    <div ref={rootRef} className="relative flex-shrink-0">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 rounded-full"
        style={{
          padding: soloId != null ? "2px 8px 2px 2px" : "4px 10px",
          background: "var(--sf-panel)",
          border: `1.5px solid ${soloId != null ? soloColor : "var(--sf-border-soft)"}`,
          cursor: "pointer",
          fontFamily: "var(--sf-font-ui)",
        }}
        title={soloId != null ? soloName : "Filter by cleaner"}
      >
        {soloId != null ? (
          <>
            <SfAvatar
              initials={soloInitials}
              color={soloColor}
              size={22}
              style={{ fontSize: 9.5, fontWeight: 700 }}
            />
            <span
              className="text-[11.5px] font-semibold text-[var(--sf-ink)]"
              style={{ maxWidth: 110, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
            >
              {firstPlusInitial(soloName) || "Cleaner"}
            </span>
          </>
        ) : (
          <span className="text-[11.5px] font-semibold text-[var(--sf-ink-2)]">
            All cleaners
          </span>
        )}
        <ChevronDown size={13} className="text-[var(--sf-ink-3)]" />
      </button>

      {open && (
        <div
          className="absolute z-50 mt-1 rounded-lg"
          style={{
            top: "100%",
            left: 0,
            minWidth: 220,
            maxHeight: 340,
            overflowY: "auto",
            background: "var(--sf-panel)",
            border: "1px solid var(--sf-border-soft)",
            boxShadow: "0 10px 30px rgba(15,23,42,.12)",
            padding: 4,
          }}
        >
          {soloId != null && (
            <>
              <button
                type="button"
                onClick={() => { toggleCleaner(soloId); setOpen(false) }}
                className="w-full flex items-center gap-2 rounded-md"
                style={{
                  padding: "6px 8px",
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  fontFamily: "var(--sf-font-ui)",
                  fontSize: 11.5,
                  fontWeight: 600,
                  color: "var(--sf-ink-2)",
                  textAlign: "left",
                }}
              >
                Show all cleaners
              </button>
              <div style={{ height: 1, background: "var(--sf-border-soft)", margin: "4px 2px" }} />
            </>
          )}
          {cleaners.map((id) => {
            const active = soloId === id
            const color = cleanerColor(id)
            const name = resolveName(id, "") || `Cleaner ${id}`
            const initials = sfInitials(name) || "?"
            return (
              <button
                key={id}
                type="button"
                onClick={() => { toggleCleaner(id); setOpen(false) }}
                className="w-full flex items-center gap-2 rounded-md"
                style={{
                  padding: "5px 8px",
                  background: active ? "var(--sf-panel-alt)" : "transparent",
                  border: "none",
                  cursor: "pointer",
                  fontFamily: "var(--sf-font-ui)",
                  opacity: soloId == null || active ? 1 : 0.5,
                  transition: "opacity .12s, background .12s",
                  textAlign: "left",
                }}
                onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = "var(--sf-panel-alt)" }}
                onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = "transparent" }}
              >
                <SfAvatar
                  initials={initials}
                  color={color}
                  size={22}
                  style={{ fontSize: 9.5, fontWeight: 700 }}
                />
                <span
                  className="text-[12px] font-semibold flex-1"
                  style={{
                    color: active ? "var(--sf-ink)" : "var(--sf-ink-2)",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {name}
                </span>
                {active && <Check size={13} className="text-[var(--sf-ink-2)]" />}
              </button>
            )
          })}
          {cleaners.length === 0 && (
            <div className="px-2 py-3 text-[11.5px] text-[var(--sf-ink-3)]">
              No cleaners available
            </div>
          )}
        </div>
      )}
    </div>
  )
}

const ScheduleToolbar = ({
  view, setView, anchor, setAnchor, nudge,
  cleaners, cleanerColor, resolveName, isSelected, toggleCleaner,
}) => {
  return (
    <div className="px-4 sm:px-6 lg:px-8 pt-3 pb-2 flex items-center gap-2">
      <DateNavigator view={view} anchor={anchor} setAnchor={setAnchor} nudge={nudge} />

      {cleaners.length > 0 ? (
        <CleanerDropdown
          cleaners={cleaners}
          cleanerColor={cleanerColor}
          resolveName={resolveName}
          isSelected={isSelected}
          toggleCleaner={toggleCleaner}
        />
      ) : null}

      <div className="flex-1" />

      <ViewSwitcher view={view} setView={setView} />
    </div>
  )
}

// ── Week view ──────────────────────────────────────────────

// Mobile-only week view: a tap-able list of 7 day cards with job
// count + revenue. Tapping a card switches to the Day view for that
// date — much more usable on phones than compressing a 7-column
// time grid into 360px.
const WeekViewMobile = ({ anchor, jobs, onPickDay }) => {
  const weekStart = useMemo(() => startOfWeek(anchor), [anchor])
  const days = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)),
    [weekStart]
  )
  const jobsByDay = useMemo(() => {
    const map = new Map()
    days.forEach((d) => map.set(d.toDateString(), []))
    jobs.forEach((j) => {
      const d = jobStartDateTime(j)
      if (!d) return
      const key = startOfDay(d).toDateString()
      if (map.has(key)) map.get(key).push(j)
    })
    return map
  }, [jobs, days])
  const today = startOfDay(new Date())

  return (
    <SfCard padding={0}>
      <div>
        {days.map((d, i) => {
          const isToday = sameDay(d, today)
          const dayJobs = jobsByDay.get(d.toDateString()) || []
          const value = dayJobs.reduce(
            (s, j) => s + (parseFloat(j.total || j.service_price || 0) || 0),
            0
          )
          return (
            <button
              key={i}
              type="button"
              onClick={() => onPickDay(d)}
              className="w-full flex items-center gap-3 text-left"
              style={{
                padding: "14px 16px",
                borderBottom:
                  i < days.length - 1
                    ? "1px solid var(--sf-border-soft)"
                    : "none",
                background: isToday ? "var(--sf-blue-soft)" : "transparent",
                WebkitTapHighlightColor: "transparent",
              }}
            >
              <div
                style={{
                  minWidth: 56,
                  textAlign: "center",
                }}
              >
                <div
                  className="text-[10.5px] font-bold uppercase"
                  style={{
                    color: isToday
                      ? "var(--sf-blue-dark)"
                      : "var(--sf-ink-3)",
                    letterSpacing: ".06em",
                  }}
                >
                  {d.toLocaleDateString("en-US", { weekday: "short" })}
                </div>
                <div
                  className={`text-[24px] leading-tight ${
                    isToday ? "font-bold" : "font-semibold"
                  }`}
                  style={{
                    color: isToday ? "var(--sf-blue-dark)" : "var(--sf-ink)",
                    letterSpacing: "-0.02em",
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {d.getDate()}
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <div
                  className="text-[13.5px] font-semibold"
                  style={{ color: "var(--sf-ink)" }}
                >
                  {dayJobs.length === 0
                    ? "No jobs"
                    : `${dayJobs.length} job${dayJobs.length === 1 ? "" : "s"}`}
                  {value > 0 && (
                    <span
                      className="font-medium"
                      style={{
                        color: "var(--sf-ink-2)",
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {" · $"}
                      {Math.round(value).toLocaleString()}
                    </span>
                  )}
                </div>
                {isToday && (
                  <div
                    className="text-[10px] font-bold mt-0.5"
                    style={{
                      color: "var(--sf-blue-dark)",
                      fontFamily: "var(--sf-font-mono)",
                      letterSpacing: ".08em",
                    }}
                  >
                    TODAY
                  </div>
                )}
              </div>
              <ChevronRight
                size={18}
                style={{ color: "var(--sf-ink-3)", flexShrink: 0 }}
              />
            </button>
          )
        })}
      </div>
    </SfCard>
  )
}

const WeekView = ({ anchor, jobs, cleanerColor, resolveName, onJobClick, onJobContextMenu }) => {
  const weekStart = useMemo(() => startOfWeek(anchor), [anchor])
  const days = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)),
    [weekStart]
  )

  // Bucket jobs per day
  const jobsByDay = useMemo(() => {
    const map = new Map()
    days.forEach((d) => map.set(d.toDateString(), []))
    jobs.forEach((j) => {
      const d = jobStartDateTime(j)
      if (!d) return
      const key = startOfDay(d).toDateString()
      if (map.has(key)) map.get(key).push(j)
    })
    return map
  }, [jobs, days])

  const startHr = 7
  const endHr = 20
  const hours = []
  for (let h = startHr; h <= endHr; h++) hours.push(h)
  const today = startOfDay(new Date())
  const nowMins = new Date().getHours() * 60 + new Date().getMinutes()

  return (
    <SfCard padding={0}>
      {/* Day headers */}
      <div className="flex border-b border-[var(--sf-border-soft)]">
        <div
          style={{ width: 56, padding: "10px 8px", borderRight: "1px solid var(--sf-border-soft)" }}
          className="text-[10px] text-[var(--sf-ink-3)] font-bold uppercase text-right"
        >
          {Intl.DateTimeFormat().resolvedOptions().timeZone?.split("/").pop()?.slice(0, 3) || "TZ"}
        </div>
        {days.map((d, i) => {
          const isToday = sameDay(d, today)
          const dayJobs = jobsByDay.get(d.toDateString()) || []
          const value = dayJobs.reduce((s, j) => s + (parseFloat(j.total || j.service_price || 0) || 0), 0)
          return (
            <div
              key={i}
              className="flex-1"
              style={{
                padding: "10px 12px",
                borderRight: i < 6 ? "1px solid var(--sf-border-soft)" : "none",
                background: isToday ? "var(--sf-blue-soft)" : "transparent",
              }}
            >
              <div className="flex items-baseline gap-2">
                <span
                  className="text-[10.5px] font-bold uppercase"
                  style={{
                    color: isToday ? "var(--sf-blue-dark)" : "var(--sf-ink-3)",
                    letterSpacing: ".06em",
                  }}
                >
                  {d.toLocaleDateString("en-US", { weekday: "short" })}
                </span>
                <span
                  className={`text-[16px] ${isToday ? "font-bold" : "font-semibold"}`}
                  style={{
                    color: isToday ? "var(--sf-blue-dark)" : "var(--sf-ink)",
                    letterSpacing: "-0.01em",
                  }}
                >
                  {d.getDate()}
                </span>
                {isToday && (
                  <span
                    className="text-[9.5px] font-bold"
                    style={{ color: "var(--sf-blue-dark)", fontFamily: "var(--sf-font-mono)" }}
                  >
                    TODAY
                  </span>
                )}
              </div>
              <div
                className="text-[10.5px] text-[var(--sf-ink-3)] mt-0.5"
                style={{ fontVariantNumeric: "tabular-nums" }}
              >
                {dayJobs.length} job{dayJobs.length === 1 ? "" : "s"}
                {value > 0 && ` · $${Math.round(value).toLocaleString()}`}
              </div>
            </div>
          )
        })}
      </div>

      {/* Time grid */}
      <div className="flex" style={{ height: 560, overflow: "hidden" }}>
        <div
          style={{ width: 56, borderRight: "1px solid var(--sf-border-soft)", position: "relative" }}
        >
          {hours.map((h, i) => (
            <div
              key={h}
              style={{
                position: "absolute",
                top: `${(i / (hours.length - 1)) * 100}%`,
                left: 0,
                right: 0,
                textAlign: "right",
                paddingRight: 8,
                fontSize: 10,
                color: "var(--sf-ink-3)",
                fontVariantNumeric: "tabular-nums",
                transform: "translateY(-50%)",
                fontWeight: 500,
              }}
            >
              {formatHourLabel(h)}
            </div>
          ))}
        </div>

        {days.map((d, dayIdx) => {
          const isToday = sameDay(d, today)
          const dayJobs = jobsByDay.get(d.toDateString()) || []
          return (
            <div
              key={dayIdx}
              style={{
                flex: 1,
                position: "relative",
                borderRight: dayIdx < 6 ? "1px solid var(--sf-border-soft)" : "none",
                background: isToday ? "#FAFCFF" : "transparent",
              }}
            >
              {hours.map((h, i) => (
                <div
                  key={h}
                  style={{
                    position: "absolute",
                    top: `${(i / (hours.length - 1)) * 100}%`,
                    left: 0,
                    right: 0,
                    borderBottom: `1px ${i % 2 === 0 ? "solid" : "dashed"} var(--sf-border-soft)`,
                    opacity: i % 2 === 0 ? 1 : 0.6,
                  }}
                />
              ))}
              {isToday && (
                <div
                  style={{
                    position: "absolute",
                    top: `${((nowMins / 60 - startHr) / (endHr - startHr)) * 100}%`,
                    left: 0,
                    right: 0,
                    height: 2,
                    background: "var(--sf-red)",
                    zIndex: 3,
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      left: -4,
                      top: -3,
                      width: 8,
                      height: 8,
                      borderRadius: 4,
                      background: "var(--sf-red)",
                    }}
                  />
                </div>
              )}
              {layoutDay(dayJobs, startHr, endHr, 560).map((item) => (
                <ScheduleBlock
                  key={item.job.id}
                  job={item.job}
                  top={item.top}
                  height={item.height}
                  cleanerColor={cleanerColor}
                  resolveName={resolveName}
                  onClick={() => onJobClick(item.job)}
                  onContextMenu={(e) => onJobContextMenu?.(item.job, e)}
                />
              ))}
            </div>
          )
        })}
      </div>
    </SfCard>
  )
}

// ── Day view ───────────────────────────────────────────────
// Job-oriented list: one row per unique job, sorted by start time. Cleaner
// avatars render inside each row so team jobs appear once (not duplicated
// per assignee) and the visible count matches the "N jobs today" number.

const DayView = ({ anchor, jobs, cleanerColor, resolveName, onJobClick, onJobContextMenu }) => {
  const day = useMemo(() => startOfDay(anchor), [anchor])

  const dayJobs = useMemo(
    () => jobs
      .filter((j) => {
        const d = jobStartDateTime(j)
        return d && sameDay(startOfDay(d), day)
      })
      .sort((a, b) => (jobStartDateTime(a)?.getTime() ?? Infinity) - (jobStartDateTime(b)?.getTime() ?? Infinity)),
    [jobs, day]
  )

  const teamCount = useMemo(
    () => dayJobs.filter((j) => assigneesFor(j).length >= 2).length,
    [dayJobs]
  )
  const revenue = useMemo(
    () => dayJobs.reduce((s, j) => s + (parseFloat(j.total || j.service_price || 0) || 0), 0),
    [dayJobs]
  )

  const isToday = sameDay(day, startOfDay(new Date()))
  const nowTs = new Date().getTime()

  if (dayJobs.length === 0) {
    return (
      <SfCard>
        <div className="py-12 text-center text-[12.5px] text-[var(--sf-ink-3)]">
          No jobs scheduled for {day.toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" })}.
        </div>
      </SfCard>
    )
  }

  return (
    <SfCard padding={0}>
      {/* Header */}
      <div className="flex items-center border-b border-[var(--sf-border-soft)] px-4 py-3">
        <div className="flex-1 min-w-0">
          <div className="text-[13.5px] font-semibold text-[var(--sf-ink)]" style={{ letterSpacing: "-0.005em" }}>
            {day.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}
            {isToday && (
              <span
                className="ml-2 text-[9.5px] font-bold align-middle"
                style={{
                  color: "#fff",
                  background: "var(--sf-blue)",
                  padding: "1px 5px",
                  borderRadius: 3,
                  fontFamily: "var(--sf-font-mono)",
                }}
              >
                TODAY
              </span>
            )}
          </div>
          <div className="text-[11.5px] text-[var(--sf-ink-3)] mt-0.5">
            {dayJobs.length} job{dayJobs.length === 1 ? "" : "s"}
            {teamCount > 0 && ` · ${teamCount} team${teamCount === 1 ? "" : "s"}`}
            {revenue > 0 && ` · $${Math.round(revenue).toLocaleString()}`}
          </div>
        </div>
      </div>

      {/* Job list */}
      <div>
        {dayJobs.map((j) => {
          const start = jobStartDateTime(j)
          const dur = durationMinutes(j)
          const assignees = assigneesFor(j)
          const isTeamJob = assignees.length >= 2
          const isUnassigned = assignees.length === 0
          const endTs = start ? start.getTime() + dur * 60000 : 0
          const startTs = start ? start.getTime() : 0
          const isPast = isToday && endTs < nowTs
          const isCurrent = isToday && startTs <= nowTs && endTs > nowTs
          const live = isLiveJob(j)
          const chipColors = sfJobChipColors(assignees.map((x) => x.id), cleanerColor)
          const jobColor = isUnassigned ? "#DC2626" : chipColors[0]
          const accentBg = isTeamJob
            ? `linear-gradient(to bottom, ${chipColors[0]} 50%, ${chipColors[1]} 50%)`
            : jobColor
          const customer = customerLabelForJob(j)
          const service = j.service_name || j.service?.name || j.title || "Service"
          const durLabel = dur >= 60
            ? `${Math.floor(dur / 60)}h${dur % 60 ? ` ${dur % 60}m` : ""}`
            : `${dur}m`
          // Per-cleaner share (visible on team jobs) — how much of the
          // job's clock-time each cleaner effectively covers.
          const shareMins = isTeamJob ? Math.round(dur / assignees.length) : dur
          const shareLabel = shareMins >= 60
            ? `${Math.floor(shareMins / 60)}h${shareMins % 60 ? ` ${shareMins % 60}m` : ""}`
            : `${shareMins}m`

          return (
            <button
              key={j.id}
              onClick={() => onJobClick(j)}
              onContextMenu={(e) => onJobContextMenu?.(j, e)}
              className="w-full flex items-stretch gap-3 px-4 py-3 border-b border-[var(--sf-border-soft)] hover:bg-[var(--sf-panel-alt)] transition-colors"
              style={{
                background: isCurrent ? "var(--sf-blue-soft)" : "transparent",
                textAlign: "left",
                cursor: "pointer",
                border: "none",
                borderBottom: "1px solid var(--sf-border-soft)",
                opacity: isPast ? 0.55 : 1,
                fontFamily: "var(--sf-font-ui)",
              }}
            >
              {/* Color spine */}
              <div
                style={{
                  width: 3,
                  borderRadius: 2,
                  background: accentBg,
                  flexShrink: 0,
                }}
              />

              {/* Time */}
              <div style={{ width: 100, flexShrink: 0 }}>
                <div
                  className="text-[13px] font-semibold text-[var(--sf-ink)]"
                  style={{
                    fontVariantNumeric: "tabular-nums",
                    lineHeight: 1.2,
                    display: "flex",
                    alignItems: "center",
                    gap: 5,
                  }}
                >
                  <StatusSign job={j} size={14} />
                  {formatJobTime(j)}
                </div>
                <div
                  className="text-[10.5px] text-[var(--sf-ink-3)] mt-0.5"
                  style={{ fontVariantNumeric: "tabular-nums" }}
                >
                  {durLabel}
                </div>
              </div>

              {/* Middle: customer + service */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 min-w-0 flex-wrap">
                  <span className="text-[13.5px] font-semibold text-[var(--sf-ink)] truncate">
                    {customer}
                  </span>
                  {isTeamJob && (
                    <span
                      className="text-[9px] font-bold flex-shrink-0"
                      style={{
                        color: "#fff",
                        background: jobColor,
                        padding: "1px 5px",
                        borderRadius: 3,
                        fontFamily: "var(--sf-font-mono)",
                        letterSpacing: ".04em",
                      }}
                      title={`Team of ${assignees.length} · ${shareLabel} share per cleaner`}
                    >
                      TEAM · {assignees.length} · {shareLabel} each
                    </span>
                  )}
                  {isUnassigned && (
                    <span
                      className="text-[9px] font-bold flex-shrink-0"
                      style={{
                        color: "#fff",
                        background: "var(--sf-red)",
                        padding: "1px 5px",
                        borderRadius: 3,
                        fontFamily: "var(--sf-font-mono)",
                      }}
                    >
                      UNASSIGNED
                    </span>
                  )}
                  {live && (
                    <span
                      className="text-[9px] font-bold flex-shrink-0"
                      style={{
                        color: "#fff",
                        background: jobColor,
                        padding: "1px 5px",
                        borderRadius: 3,
                        fontFamily: "var(--sf-font-mono)",
                      }}
                    >
                      LIVE
                    </span>
                  )}
                  {isCurrent && !live && (
                    <span
                      className="text-[9px] font-bold flex-shrink-0"
                      style={{
                        color: "var(--sf-red-dark)",
                        background: "var(--sf-panel)",
                        border: "1px solid var(--sf-red)",
                        padding: "0 4px",
                        borderRadius: 3,
                        fontFamily: "var(--sf-font-mono)",
                      }}
                    >
                      NOW
                    </span>
                  )}
                </div>
                <div className="text-[11.5px] text-[var(--sf-ink-2)] mt-0.5 truncate">
                  {service}
                </div>
              </div>

              {/* Right: cleaner chips */}
              <div className="flex items-center gap-2 flex-shrink-0">
                {isUnassigned ? (
                  <div
                    style={{
                      width: 30,
                      height: 30,
                      borderRadius: 15,
                      background: "var(--sf-red-soft)",
                      color: "var(--sf-red-dark)",
                      fontSize: 9.5,
                      fontWeight: 700,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      border: "1.5px dashed var(--sf-red)",
                    }}
                    title="Unassigned"
                  >
                    —
                  </div>
                ) : (
                  assignees.map((a) => {
                    const name = resolveName(a.id, a.name) || `Cleaner ${a.id}`
                    const first = firstPlusInitial(name)
                    return (
                      <div
                        key={a.id}
                        className="flex items-center gap-1.5"
                        title={name}
                      >
                        <SfAvatar
                          initials={sfInitials(name) || "?"}
                          color={cleanerColor(a.id)}
                          size={26}
                        />
                        <span
                          className="text-[11.5px] font-semibold text-[var(--sf-ink)]"
                          style={{ maxWidth: 88, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                        >
                          {first}
                        </span>
                      </div>
                    )
                  })
                )}
              </div>
            </button>
          )
        })}
      </div>
    </SfCard>
  )
}

// ── Month view ─────────────────────────────────────────────

const MonthView = ({ anchor, jobs, cleanerColor, resolveName, onJobClick, onPickDay, onJobContextMenu }) => {
  const monthStart = useMemo(() => {
    const x = new Date(anchor)
    x.setDate(1)
    x.setHours(0, 0, 0, 0)
    return x
  }, [anchor])
  const gridStart = useMemo(() => startOfWeek(monthStart), [monthStart])
  const cells = useMemo(
    () => Array.from({ length: 42 }, (_, i) => addDays(gridStart, i)),
    [gridStart]
  )

  const jobsByDay = useMemo(() => {
    const map = new Map()
    jobs.forEach((j) => {
      const d = jobStartDateTime(j)
      if (!d) return
      const key = startOfDay(d).toDateString()
      if (!map.has(key)) map.set(key, [])
      map.get(key).push(j)
    })
    return map
  }, [jobs])

  // Cells the user has clicked "+N more" on — those render all jobs
  // inline instead of navigating to the day view.
  const [expandedCells, setExpandedCells] = useState(() => new Set())
  const toggleExpand = useCallback((key) => {
    setExpandedCells((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }, [])

  const today = startOfDay(new Date())

  return (
    <SfCard padding={0}>
      {/* Headers */}
      <div
        className="grid"
        style={{
          gridTemplateColumns: "repeat(7, 1fr)",
          borderBottom: "1px solid var(--sf-border-soft)",
          background: "var(--sf-panel-alt)",
        }}
      >
        {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d, i) => (
          <div
            key={d}
            className="text-[10.5px] text-[var(--sf-ink-3)] font-bold uppercase"
            style={{
              padding: "10px 12px",
              borderRight: i < 6 ? "1px solid var(--sf-border-soft)" : "none",
              letterSpacing: ".06em",
            }}
          >
            {d}
          </div>
        ))}
      </div>
      <div
        className="grid"
        style={{ gridTemplateColumns: "repeat(7, 1fr)", gridAutoRows: "minmax(160px, auto)" }}
      >
        {cells.map((d, idx) => {
          const col = idx % 7
          const row = Math.floor(idx / 7)
          const isOtherMonth = d.getMonth() !== monthStart.getMonth()
          const isToday = sameDay(d, today)
          const dateKey = d.toDateString()
          const dayJobs = jobsByDay.get(dateKey) || []
          const isExpanded = expandedCells.has(dateKey)
          const visibleJobs = isExpanded ? dayJobs : dayJobs.slice(0, 3)
          return (
            <div
              key={idx}
              onClick={() => onPickDay?.(d)}
              style={{
                padding: "7px 9px",
                borderRight: col < 6 ? "1px solid var(--sf-border-soft)" : "none",
                borderBottom: row < 5 ? "1px solid var(--sf-border-soft)" : "none",
                background: isToday
                  ? "var(--sf-blue-soft)"
                  : isOtherMonth
                  ? "var(--sf-panel-alt)"
                  : "transparent",
                display: "flex",
                flexDirection: "column",
                gap: 3,
                minHeight: 160,
                cursor: "pointer",
              }}
            >
              <div className="flex items-center gap-1.5">
                <span
                  className={isToday ? "text-[13px] font-bold" : "text-[13px] font-semibold"}
                  style={{
                    color: isOtherMonth
                      ? "var(--sf-ink-4)"
                      : isToday
                      ? "var(--sf-blue-dark)"
                      : "var(--sf-ink)",
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {d.getDate()}
                </span>
                {d.getDate() === 1 && (
                  <span
                    className="text-[9.5px] text-[var(--sf-ink-3)] font-semibold uppercase"
                    style={{ letterSpacing: ".04em" }}
                  >
                    {d.toLocaleDateString("en-US", { month: "short" })}
                  </span>
                )}
                {isToday && (
                  <span
                    className="text-[9px] font-bold"
                    style={{
                      color: "#fff",
                      background: "var(--sf-blue)",
                      padding: "1px 5px",
                      borderRadius: 3,
                      fontFamily: "var(--sf-font-mono)",
                    }}
                  >
                    TODAY
                  </span>
                )}
                <div className="flex-1" />
                {dayJobs.length > 0 && (
                  <span
                    className="text-[10px] text-[var(--sf-ink-3)] font-semibold"
                    style={{ fontVariantNumeric: "tabular-nums" }}
                  >
                    {dayJobs.length}
                  </span>
                )}
              </div>
              {visibleJobs.map((j) => {
                const a = assigneesFor(j)
                const chipColors = sfJobChipColors(a.map((x) => x.id), cleanerColor)
                const color = chipColors[0]
                const isTeam = chipColors.length >= 2
                const statusKey = normalizeJobStatus(j)
                const sign = jobStatusSign(j)
                const isActive =
                  statusKey === "en_route" || statusKey === "in_progress"
                const dur = durationMinutes(j)
                const durLabel = dur >= 60
                  ? `${Math.floor(dur / 60)}h${dur % 60 ? ` ${dur % 60}m` : ""}`
                  : `${dur}m`
                const cleanerNames = a
                  .map((x) => {
                    const full = resolveName ? resolveName(x.id, x.name) : x.name
                    return firstPlusInitial(full)
                  })
                  .filter(Boolean)
                const cleanerLabel = cleanerNames.length === 0
                  ? "Unassigned"
                  : cleanerNames.length <= 2
                  ? cleanerNames.join(", ")
                  : `${cleanerNames.slice(0, 2).join(", ")} +${cleanerNames.length - 2}`
                return (
                  <button
                    key={j.id}
                    onClick={(e) => { e.stopPropagation(); onJobClick(j) }}
                    onContextMenu={(e) => { e.stopPropagation(); onJobContextMenu?.(j, e) }}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                      padding: "3px 6px",
                      // Status = sign only — chip fill stays soft (cleaner tint), not solid live paint.
                      // Team jobs render a diagonal two-tone fill using both cleaners' tints so
                      // the same team always looks the same regardless of assignee order.
                      background: statusKey === "completed"
                        ? "var(--sf-panel-soft)"
                        : isTeam
                        ? `linear-gradient(135deg, ${chipColors[0]}14 0%, ${chipColors[0]}14 50%, ${chipColors[1]}14 50%, ${chipColors[1]}14 100%)`
                        : `${color}14`,
                      color: "var(--sf-ink)",
                      borderTop: "none",
                      borderRight: "none",
                      borderBottom: "none",
                      borderLeft: `2px solid ${isActive ? sign.color : color}`,
                      borderRadius: 3,
                      cursor: "pointer",
                      fontFamily: "var(--sf-font-ui)",
                      textAlign: "left",
                      overflow: "hidden",
                      opacity: statusKey === "completed" ? 0.85 : 1,
                    }}
                  >
                    <span
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 4,
                        fontVariantNumeric: "tabular-nums",
                        fontFamily: "var(--sf-font-mono)",
                        fontSize: 9.5,
                        fontWeight: 600,
                        color: "var(--sf-ink-2)",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        minWidth: 0,
                      }}
                    >
                      <StatusSign job={j} size={12} />
                      {isNewJob(j) && <NewBadge />}
                      {isRecurringJob(j) && <RecurringChip size={12} />}
                      <span
                        style={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {formatJobTime(j)} · {durLabel}
                      </span>
                    </span>
                    <span
                      style={{
                        display: "block",
                        fontSize: 10.5,
                        fontWeight: 600,
                        color: "var(--sf-ink)",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {customerLabelForJob(j)}
                    </span>
                    <span
                      style={{
                        display: "block",
                        fontSize: 9.5,
                        fontWeight: 500,
                        color: "var(--sf-ink-3)",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {cleanerLabel}
                    </span>
                  </button>
                )
              })}
              {dayJobs.length > 3 && (
                <button
                  onClick={(e) => { e.stopPropagation(); toggleExpand(dateKey) }}
                  className="text-[10.5px] text-[var(--sf-ink-3)] hover:text-[var(--sf-ink)] font-semibold text-left"
                  style={{
                    padding: "2px 5px",
                    background: "transparent",
                    border: "none",
                    cursor: "pointer",
                    fontFamily: "var(--sf-font-ui)",
                  }}
                >
                  {isExpanded ? "− show less" : `+${dayJobs.length - 3} more`}
                </button>
              )}
            </div>
          )
        })}
      </div>
    </SfCard>
  )
}

// ── Schedule block (used by Day + Week views) ──────────────

const ScheduleBlock = ({ job, top, height, cleanerColor, forcedColor, resolveName, onClick, onContextMenu }) => {
  const assignees = assigneesFor(job)
  const chipColors = sfJobChipColors(assignees.map((x) => x.id), cleanerColor)
  const color = forcedColor || chipColors[0]
  const isTeam = !forcedColor && chipColors.length >= 2
  const statusKey = normalizeJobStatus(job)
  const sign = jobStatusSign(job)
  const isActive = statusKey === "en_route" || statusKey === "in_progress"
  // Full customer name (issue #34) — truncate via CSS, don't shorten to first name
  const customer = customerLabelForJob(job) || "—"
  const startMins = (() => {
    const d = jobStartDateTime(job)
    return d ? d.getHours() * 60 + d.getMinutes() : 0
  })()
  const dur = durationMinutes(job)
  const durLabel = dur >= 60
    ? `${Math.floor(dur / 60)}h${dur % 60 ? ` ${dur % 60}m` : ""}`
    : `${dur}m`
  // Match Month view's cleaner label (first + initial, up to 2 + overflow).
  // Must go through resolveName — assignee rows in /jobs often arrive as
  // { id } only, with the display name hydrated by the parent's lookup map.
  // The prior `a.name.split(" ")` path blanked out for everyone, which is
  // why week cards had no cleaner at all.
  const cleanerNames = assignees
    .map((a) => firstPlusInitial(resolveName ? resolveName(a.id, a.name) : a.name))
    .filter(Boolean)
  const cleanerLabel = cleanerNames.length === 0
    ? "Unassigned"
    : cleanerNames.length <= 2
    ? cleanerNames.join(", ")
    : `${cleanerNames.slice(0, 2).join(", ")} +${cleanerNames.length - 2}`

  return (
    <button
      onClick={onClick}
      onContextMenu={onContextMenu}
      className="sf-timeline-block"
      title={`${sign.label} · ${customer}`}
      style={{
        position: "absolute",
        top: `${top}px`,
        height: `${height}px`,
        left: 4,
        right: 4,
        display: "flex",
        flexDirection: "column",
        padding: 0,
        // Match month: soft chip, status via sign only (not solid live fill).
        // Team jobs render the 3px left border as a vertical two-tone (both
        // cleaners' colors) so the same team always looks the same regardless
        // of assignee order.
        background: statusKey === "completed" ? "var(--sf-panel-soft)" : "#fff",
        backgroundImage: isTeam && !isActive
          ? `linear-gradient(to bottom, ${chipColors[0]} 50%, ${chipColors[1]} 50%)`
          : undefined,
        backgroundRepeat: isTeam && !isActive ? "no-repeat" : undefined,
        backgroundSize: isTeam && !isActive ? "3px 100%" : undefined,
        backgroundPosition: isTeam && !isActive ? "left top" : undefined,
        backgroundOrigin: isTeam && !isActive ? "border-box" : undefined,
        backgroundClip: isTeam && !isActive ? "border-box" : undefined,
        borderTop: "1px solid var(--sf-border-soft)",
        borderRight: "1px solid var(--sf-border-soft)",
        borderBottom: "1px solid var(--sf-border-soft)",
        borderLeft: isTeam && !isActive
          ? "3px solid transparent"
          : `3px solid ${isActive ? sign.color : color}`,
        color: "var(--sf-ink)",
        borderRadius: 4,
        cursor: "pointer",
        fontFamily: "var(--sf-font-ui)",
        textAlign: "left",
        boxShadow: "var(--sf-shadow)",
        zIndex: 2,
        overflow: "hidden",
        opacity: statusKey === "completed" ? 0.88 : 1,
      }}
    >
      {/* Two-line header, always rendered — HEADER_HEIGHT_PX in layoutDay
          matches this box so overlapping blocks cascade past both rows
          instead of only the top one. Line 1: time · duration · customer.
          Line 2: cleaner names (comma-joined, same style as month view). */}
      <div
        style={{
          minHeight: HEADER_HEIGHT_PX,
          flexShrink: 0,
          display: "flex",
          flexDirection: "column",
          gap: 1,
          padding: "3px 6px 2px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 5, minWidth: 0 }}>
          <StatusSign job={job} size={12} />
          {isNewJob(job) && <NewBadge />}
          {isRecurringJob(job) && <RecurringChip size={12} />}
          <span
            style={{
              fontSize: 9.5,
              color: "var(--sf-ink-3)",
              fontFamily: "var(--sf-font-mono)",
              fontVariantNumeric: "tabular-nums",
              flexShrink: 0,
            }}
          >
            {minsToLabel(startMins)} · {durLabel}
          </span>
          <span
            style={{
              fontSize: 10.5,
              fontWeight: 600,
              flex: 1,
              minWidth: 0,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              color: "var(--sf-ink)",
            }}
          >
            {customer}
          </span>
        </div>
        <div
          style={{
            fontSize: 9.5,
            fontWeight: assignees.length === 0 ? 700 : 500,
            color: assignees.length === 0 ? "var(--sf-red-dark)" : "var(--sf-ink-3)",
            paddingLeft: 17,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {cleanerLabel}
        </div>
      </div>
    </button>
  )
}

const customerLabelForJob = (j) => {
  if (j.customer_name) return j.customer_name
  const first = j.customer_first_name || j.first_name || ""
  const last = j.customer_last_name || j.last_name || ""
  const composed = `${first} ${last}`.trim()
  return composed || j.customer?.name || j.customer_email || "Customer"
}

// "First L." when a last name exists, otherwise "First". Disambiguates
// cleaners who share a first name (e.g. two Tatianas) in cramped chips.
const firstPlusInitial = (name) => {
  if (!name) return ""
  const parts = String(name).trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return ""
  if (parts.length === 1) return parts[0]
  return `${parts[0]} ${parts[parts.length - 1][0].toUpperCase()}.`
}

const minsToLabel = (m) => {
  const h = Math.floor(m / 60)
  const mm = m % 60
  const period = h >= 12 ? "p" : "a"
  const h12 = h % 12 === 0 ? 12 : h % 12
  return mm === 0 ? `${h12}${period}` : `${h12}:${String(mm).padStart(2, "0")}${period}`
}

// ── ZB availability sync button ────────────────────────────
//
// Calls POST /api/team-availability/sync (kicks off in background) then
// polls GET /api/team-availability/sync/progress until it hits a terminal
// state. Invokes onDone(summary) on success so the parent can invalidate
// the team query and refresh cell data.
const SyncFromZBButton = ({ onDone }) => {
  const [status, setStatus] = useState("idle") // idle | running | done | error
  const [summary, setSummary] = useState(null)
  const [error, setError] = useState(null)

  const run = useCallback(async () => {
    setStatus("running")
    setError(null)
    setSummary(null)
    try {
      await teamAPI.syncAvailabilityFromZenbooker()
    } catch (e) {
      // 409 = already running elsewhere — start polling anyway
      if (e?.response?.status !== 409) {
        setStatus("error")
        setError(e?.response?.data?.error || e?.message || "Sync failed to start")
        return
      }
    }
    // Poll every 2s for up to 3 minutes
    const started = Date.now()
    while (Date.now() - started < 180_000) {
      await new Promise((r) => setTimeout(r, 2000))
      let p
      try {
        p = await teamAPI.getAvailabilitySyncProgress()
      } catch {
        continue
      }
      if (p?.status === "done") {
        setStatus("done")
        setSummary(p.summary || null)
        onDone?.(p.summary)
        return
      }
      if (p?.status === "error") {
        setStatus("error")
        setError(p.error || "Reconcile failed")
        return
      }
    }
    setStatus("error")
    setError("Timed out waiting for sync to finish")
  }, [onDone])

  const busy = status === "running"
  const updated = summary?.updated ?? 0
  const unchanged = summary?.unchanged ?? 0
  const scanned = summary?.scanned ?? 0
  const noMatch = (summary?.skipped_no_zb_id ?? 0) + (summary?.skipped_no_zb_match ?? 0)
  const doneLabel = updated > 0
    ? `✓ Updated ${updated} of ${scanned} cleaners`
    : scanned > 0
      ? `✓ Already up to date${noMatch > 0 ? ` (${noMatch} not on ZB)` : ""}`
      : "✓ Nothing to sync"

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      {status === "done" && (
        <span
          className="text-[11px]"
          style={{ color: "var(--sf-green-dark)", fontWeight: 600 }}
          title={`updated=${updated} unchanged=${unchanged} scanned=${scanned} not_on_zb=${noMatch}`}
        >
          {doneLabel}
        </span>
      )}
      {status === "error" && (
        <span
          className="text-[11px]"
          style={{ color: "var(--sf-red-dark)", fontWeight: 600 }}
          title={error || ""}
        >
          Sync failed
        </span>
      )}
      <button
        onClick={run}
        disabled={busy}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          padding: "6px 10px",
          border: "1px solid var(--sf-border-soft)",
          borderRadius: 6,
          fontSize: 12,
          fontFamily: "var(--sf-font-ui)",
          fontWeight: 600,
          background: busy ? "var(--sf-panel-soft)" : "var(--sf-panel)",
          color: busy ? "var(--sf-ink-3)" : "var(--sf-blue-dark)",
          cursor: busy ? "wait" : "pointer",
        }}
        title="Pull latest availability from Zenbooker /timeslots"
      >
        <RefreshCw
          size={12}
          style={{ animation: busy ? "sf-spin 1s linear infinite" : "none" }}
        />
        {busy ? "Syncing…" : "Sync from Zenbooker"}
      </button>
      <style>{`@keyframes sf-spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  )
}

// ── Availability view ──────────────────────────────────────

const AvailabilityView = ({
  cleaners,
  jobs,
  cleanerColor,
  resolveName,
  anchor,
  view = "week",
  setView,
  setAnchor,
  onOpenManageShift,
  territories,
  selectedTerritoryId,
  onSelectTerritory,
  onSyncFromZB,
  companyDrivingMinutes = 0,
}) => {
  const weekStart = useMemo(() => startOfWeek(anchor), [anchor])
  const days = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)),
    [weekStart]
  )
  const dateKeys = useMemo(() => days.map(formatDateKey), [days])

  // Split cleaners into bookable (real service providers) vs informative
  // (managers, schedulers, account owners). `cleaners` arrives pre-sorted
  // with bookable first, so the boundary is the index of the first row
  // that fails the predicate. Kept local so the render can render a
  // divider between groups and KPIs can aggregate over bookable only.
  const firstInformativeIdx = useMemo(() => {
    const idx = cleaners.findIndex((m) => !isBookableTeamMember(m))
    return idx < 0 ? cleaners.length : idx
  }, [cleaners])
  const bookableCleaners = useMemo(
    () => cleaners.slice(0, firstInformativeIdx),
    [cleaners, firstInformativeIdx]
  )

  // Derive per-(cleaner, day) capacity from real team_members.availability
  // + scheduled jobs. Old behavior derived a fake availability from jobs
  // alone (a cleaner with no jobs was labeled "Off"); after Aug 2026 we
  // have ZB-sourced availability jsonb on every synced team_member, so
  // "Off" now means actually not scheduled.
  //
  // Cell shape:
  //   {
  //     workingMinutes: number,   // real hours per team_members.availability
  //     bookedMinutes:  number,   // minutes of jobs assigned to this cleaner
  //     availableMinutes: number, // working minus jobs and the travel buffer around them
  //     jobs: number,             // count of jobs assigned that day
  //     offDay: boolean,          // true when availability signal says off
  //     noSignal: boolean,        // no availability data at all for this row
  //   }
  const status = useMemo(() => {
    const map = new Map()
    // Pre-build per-cleaner per-day booked intervals so we don't scan
    // every job for every cell.
    const bookedByCleaner = new Map() // cleanerId → dateKey → [[start,end], ...]
    jobs.forEach((j) => {
      const startDate = jobStartDateTime(j)
      if (!startDate) return
      const dayIdx = days.findIndex((dd) => sameDay(dd, startOfDay(startDate)))
      if (dayIdx < 0) return
      const dateKey = dateKeys[dayIdx]
      const dur = durationMinutes(j)
      const interval = jobIntervalOnDate(startDate, dur, dateKey)
      if (!interval) return
      assigneesFor(j).forEach((a) => {
        const id = String(a.id)
        if (!bookedByCleaner.has(id)) bookedByCleaner.set(id, new Map())
        const byDate = bookedByCleaner.get(id)
        if (!byDate.has(dateKey)) byDate.set(dateKey, [])
        byDate.get(dateKey).push(interval)
      })
    })
    cleaners.forEach((m) => {
      const id = String(m.id)
      const bookedForCleaner = bookedByCleaner.get(id) || new Map()
      const driving = memberDrivingMinutes(m.availability, companyDrivingMinutes)
      const row = dateKeys.map((dateKey) => {
        const working = getWorkingIntervals(m.availability, dateKey)
        const bookedIntervals = bookedForCleaner.get(dateKey) || []
        const bookedMinutes = sumIntervalMinutes(bookedIntervals)
        const jobsCount = bookedIntervals.length
        if (working === null) {
          // No availability signal at all — historical rows or manually-
          // added cleaners without a saved schedule. Distinguish from a
          // real off-day so the operator can spot rows that need setup.
          return {
            workingMinutes: 0,
            bookedMinutes,
            availableMinutes: 0,
            jobs: jobsCount,
            offDay: false,
            noSignal: true,
            freeWindows: [],
          }
        }
        if (working.length === 0) {
          return {
            workingMinutes: 0,
            bookedMinutes,
            availableMinutes: 0,
            jobs: jobsCount,
            offDay: true,
            noSignal: false,
            freeWindows: [],
          }
        }
        const workingMinutes = sumIntervalMinutes(working)
        // Travel time before and after each job isn't bookable either.
        const remaining = subtractIntervals(working, padIntervals(bookedIntervals, driving))
        const availableMinutes = sumIntervalMinutes(remaining)
        return {
          workingMinutes,
          bookedMinutes,
          availableMinutes,
          jobs: jobsCount,
          offDay: false,
          noSignal: false,
          // Actual free intervals (minute-of-day pairs) — surfaced in each
          // cell so operators see WHEN the cleaner is free, not just how
          // many hours. Same subtraction the KPI count already used, just
          // preserved instead of collapsed to a total.
          freeWindows: remaining,
        }
      })
      map.set(id, row)
    })
    return map
  }, [cleaners, jobs, days, dateKeys, companyDrivingMinutes])

  // Top KPIs — recomputed against real availability instead of a flat
  // 8h × N × 7 baseline. Capacity now reflects what cleaners actually
  // have on their schedule for the week.
  //
  // Aggregates over BOOKABLE cleaners only — managers/schedulers show in
  // the grid for reference but their shift hours don't count toward
  // tenant capacity, LB availability, or anything downstream.
  const kpis = useMemo(() => {
    let totalWorkingMinutes = 0
    let totalBookedMinutes = 0
    let totalAvailableMinutes = 0
    let onShift = 0
    bookableCleaners.forEach((m) => {
      const row = status.get(String(m.id)) || []
      let cleanerWorking = 0
      row.forEach((c) => {
        totalWorkingMinutes += c.workingMinutes
        totalBookedMinutes += c.bookedMinutes
        totalAvailableMinutes += c.availableMinutes
        cleanerWorking += c.workingMinutes
      })
      if (cleanerWorking > 0) onShift += 1
    })
    return {
      onShift,
      totalCleaners: bookableCleaners.length,
      capacityHours: Math.round(totalWorkingMinutes / 60),
      bookedHours:   Math.round(totalBookedMinutes / 60),
      availableHours: Math.round(totalAvailableMinutes / 60),
    }
  }, [bookableCleaners, status])
  const utilization = kpis.capacityHours
    ? Math.round((kpis.bookedHours / kpis.capacityHours) * 100)
    : 0
  const availableHours = kpis.availableHours

  if (cleaners.length === 0) {
    return (
      <SfCard>
        <div className="py-12 text-center text-[12.5px] text-[var(--sf-ink-3)]">
          No team members loaded.
        </div>
      </SfCard>
    )
  }

  return (
    <div className="flex flex-col gap-4">
      {/* KPIs — only meaningful in week view; day/month have their own headers. */}
      {view === "week" && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <SfKPI
            label="On shift this week"
            value={`${kpis.onShift} / ${kpis.totalCleaners}`}
            accent="var(--sf-green)"
            sub="cleaners scheduled"
          />
          <SfKPI
            label="Total capacity"
            value={`${kpis.capacityHours} hrs`}
            accent="var(--sf-blue)"
            sub={`${kpis.totalCleaners} cleaner${kpis.totalCleaners === 1 ? "" : "s"} · real hours`}
          />
          <SfKPI
            label="Booked"
            value={`${kpis.bookedHours} hrs`}
            accent="var(--sf-purple)"
            sub={`${utilization}% utilization`}
          />
          <SfKPI
            label="Available"
            value={`${availableHours} hrs`}
            accent="var(--sf-amber)"
            sub="open for booking"
          />
        </div>
      )}

      {view === "week" && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[11px] text-[var(--sf-ink-3)] italic">
            Click any cell to assign / reassign jobs
          </span>
        </div>
      )}

      {view === "day" ? (
        <AvailabilityDayView
          anchor={anchor}
          cleaners={cleaners}
          jobs={jobs}
          cleanerColor={cleanerColor}
          onOpenManageShift={onOpenManageShift}
          companyDrivingMinutes={companyDrivingMinutes}
        />
      ) : view === "month" ? (
        <AvailabilityMonthView
          anchor={anchor}
          cleaners={cleaners}
          jobs={jobs}
          companyDrivingMinutes={companyDrivingMinutes}
          onPickDay={(d) => { setAnchor?.(d); setView?.("day") }}
        />
      ) : (
      <>
      {/* Grid */}
      <SfCard padding={0}>
        <div
          className="flex items-center"
          style={{ padding: "14px 18px", borderBottom: "1px solid var(--sf-border-soft)" }}
        >
          <div>
            <div className="text-[13.5px] font-semibold text-[var(--sf-ink)]">
              Team availability
            </div>
            <div className="text-[11.5px] text-[var(--sf-ink-3)] mt-px">
              {formatRangeLabel("week", anchor)} · from cleaner working hours
              {selectedTerritoryId != null ? (
                <>
                  {" · "}
                  <span style={{ color: "var(--sf-blue-dark)" }}>
                    {territories?.find((t) => Number(t.id) === Number(selectedTerritoryId))?.name || "Territory"}
                  </span>
                </>
              ) : null}
            </div>
          </div>
          <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 12 }}>
            {Array.isArray(territories) && territories.length > 0 && (
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <label
                  htmlFor="availability-territory-picker"
                  className="text-[11px] text-[var(--sf-ink-3)] font-semibold uppercase"
                  style={{ letterSpacing: ".05em" }}
                >
                  Territory
                </label>
                <select
                  id="availability-territory-picker"
                  value={selectedTerritoryId ?? ""}
                  onChange={(e) => {
                    const v = e.target.value
                    onSelectTerritory?.(v === "" ? null : Number(v))
                  }}
                  style={{
                    padding: "6px 10px",
                    border: "1px solid var(--sf-border-soft)",
                    borderRadius: 6,
                    fontSize: 12,
                    fontFamily: "var(--sf-font-ui)",
                    background: "var(--sf-panel)",
                    color: "var(--sf-ink)",
                    cursor: "pointer",
                    minWidth: 160,
                  }}
                >
                  <option value="">All territories</option>
                  {territories.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name || `Territory ${t.id}`}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <SyncFromZBButton onDone={onSyncFromZB} />
          </div>
        </div>

        {/* Day headers */}
        <div className="flex border-b border-[var(--sf-border-soft)] bg-[var(--sf-panel-alt)]">
          <div
            style={{ width: 180, padding: "10px 14px", borderRight: "1px solid var(--sf-border-soft)" }}
            className="text-[10.5px] text-[var(--sf-ink-3)] font-bold uppercase"
          >
            Team / Cleaner
          </div>
          {days.map((d, i) => {
            const isToday = sameDay(d, startOfDay(new Date()))
            return (
              <div
                key={i}
                className="flex-1 text-center"
                style={{
                  padding: "10px 12px",
                  borderRight: i < 6 ? "1px solid var(--sf-border-soft)" : "none",
                  background: isToday ? "var(--sf-blue-soft)" : "transparent",
                }}
              >
                <div
                  className="text-[10.5px] font-bold uppercase"
                  style={{
                    color: isToday ? "var(--sf-blue-dark)" : "var(--sf-ink-3)",
                    letterSpacing: ".06em",
                  }}
                >
                  {d.toLocaleDateString("en-US", { weekday: "short" })}
                </div>
                <div
                  className={isToday ? "text-[13px] font-bold" : "text-[13px] font-semibold"}
                  style={{ color: isToday ? "var(--sf-blue-dark)" : "var(--sf-ink)" }}
                >
                  {d.getDate()}
                </div>
              </div>
            )
          })}
        </div>

        {/* Rows */}
        {cleaners.map((m, ti) => {
          const id = m.id
          const row = status.get(String(id)) || []
          const name =
            m.name ||
            `${m.first_name || ""} ${m.last_name || ""}`.trim() ||
            m.email ||
            "Cleaner"
          const color = cleanerColor(id)
          // Weekly totals per cleaner — sum across the 7 cells in this row.
          // Shown under the name so operators can rank cleaners by real
          // remaining capacity without eyeballing 7 cells.
          const totalShiftH = row.reduce((s, c) => s + (c.workingMinutes || 0), 0) / 60
          const totalWorkedH = row.reduce((s, c) => s + (c.bookedMinutes || 0), 0) / 60
          const totalFreeH = row.reduce((s, c) => s + (c.availableMinutes || 0), 0) / 60
          const fmtHRow = (h) => {
            const r = Math.round(h * 10) / 10
            return Number.isInteger(r) ? String(r) : r.toFixed(1)
          }
          // Group divider — inserted between bookable cleaners and the
          // informative (manager/scheduler) group. `cleaners` arrives
          // pre-sorted with bookable first, so the boundary appears once
          // at `firstInformativeIdx` and the two groups need their own
          // visual affordance so operators don't misread manager rows
          // as bookable capacity.
          const showGroupDivider = ti === firstInformativeIdx && firstInformativeIdx > 0
            && firstInformativeIdx < cleaners.length
          const isInformative = ti >= firstInformativeIdx && firstInformativeIdx < cleaners.length
          return (
            <React.Fragment key={id}>
              {showGroupDivider && (
                <div
                  className="flex items-center"
                  style={{
                    padding: "8px 14px",
                    background: "var(--sf-panel-soft)",
                    borderTop: "1px solid var(--sf-border-soft)",
                    borderBottom: "1px solid var(--sf-border-soft)",
                    fontSize: 10.5,
                    fontWeight: 700,
                    color: "var(--sf-ink-3)",
                    letterSpacing: ".06em",
                    textTransform: "uppercase",
                    gap: 8,
                  }}
                >
                  <span>Managers & schedulers</span>
                  <span
                    style={{
                      fontSize: 9.5,
                      fontWeight: 500,
                      color: "var(--sf-ink-3)",
                      textTransform: "none",
                      letterSpacing: 0,
                    }}
                  >
                    · informative only, not counted in team capacity or sent to LB
                  </span>
                </div>
              )}
            <div
              className="flex"
              style={{
                opacity: isInformative ? 0.68 : 1,
                background: isInformative ? "var(--sf-panel-soft)" : "transparent",
                borderBottom:
                  ti < cleaners.length - 1 ? "1px solid var(--sf-border-soft)" : "none",
              }}
            >
              <div
                style={{
                  width: 180,
                  padding: "12px 14px",
                  borderRight: "1px solid var(--sf-border-soft)",
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                }}
              >
                <div
                  style={{ width: 5, height: 32, background: color, borderRadius: 1.5, flexShrink: 0 }}
                />
                <div className="min-w-0 flex-1">
                  <div className="text-[12.5px] font-bold text-[var(--sf-ink)] truncate">
                    {name}
                  </div>
                  <div className="text-[10.5px] text-[var(--sf-ink-3)] mt-px">
                    {(m.role || "Cleaner")}
                  </div>
                  {totalShiftH > 0 && (
                    <div
                      className="text-[10px] mt-0.5 tabular-nums"
                      title={`This week: ${fmtHRow(totalFreeH)}h free of ${fmtHRow(totalShiftH)}h shift (${fmtHRow(totalWorkedH)}h booked)`}
                    >
                      <span style={{ color: "var(--sf-green-dark)", fontWeight: 600 }}>
                        {fmtHRow(totalFreeH)}h free
                      </span>
                      <span style={{ color: "var(--sf-ink-3)" }}> · </span>
                      <span style={{ color: "#B91C1C", fontWeight: 600 }}>
                        {fmtHRow(totalWorkedH)}h worked
                      </span>
                    </div>
                  )}
                </div>
              </div>
              {row.map((cell, di) => {
                const isToday = sameDay(days[di], startOfDay(new Date()))
                const shiftH = cell.workingMinutes / 60
                const workedH = cell.bookedMinutes / 60
                const freeH = cell.availableMinutes / 60
                const fmtH = (h) => {
                  const r = Math.round(h * 10) / 10
                  return Number.isInteger(r) ? String(r) : r.toFixed(1)
                }
                // Compact wall-clock: "9a", "5:30p", "12p" — dense cells
                // can't fit the "9:00 AM" formatter without wrapping every
                // slot to a second line.
                const fmtMin = (mm) => {
                  const h = Math.floor(mm / 60), mi = mm % 60
                  const suf = h >= 12 ? "p" : "a"
                  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h
                  return mi === 0 ? `${h12}${suf}` : `${h12}:${String(mi).padStart(2, "0")}${suf}`
                }
                const freeWindowsStr = (cell.freeWindows || [])
                  .map((iv) => `${fmtMin(iv[0])}–${fmtMin(iv[1])}`)
                  .join(", ")
                let meta
                // Worked-hours segment — always rendered red so operators
                // can eyeball capacity load from across the grid without
                // reading the numbers. Free portion stays in the cell's
                // status color (green/amber).
                const workedSeg = (
                  <span style={{ color: "#B91C1C", fontWeight: 700 }}>
                    {fmtH(workedH)}h worked
                  </span>
                )
                if (cell.noSignal) {
                  // No availability data at all — flag for setup, don't
                  // silently pretend "Off".
                  meta = {
                    c: "var(--sf-ink-3)",
                    bg: "var(--sf-panel-soft)",
                    icon: Minus,
                    label: "—",
                    sub: "no schedule",
                  }
                } else if (cell.offDay) {
                  // Off-day but with jobs (unusual, e.g. asked to cover a
                  // day off) still owes a worked-hours count.
                  meta = {
                    c: "var(--sf-ink-3)",
                    bg: "var(--sf-panel-soft)",
                    icon: Minus,
                    label: "Off",
                    sub: cell.jobs > 0 ? workedSeg : null,
                  }
                } else if (cell.availableMinutes === 0) {
                  // Working but fully booked — amber so it stands out from
                  // both green (free) and grey (off).
                  meta = {
                    c: "var(--sf-amber-dark)",
                    bg: "var(--sf-amber-soft)",
                    icon: Check,
                    label: `${fmtH(shiftH)}h shift`,
                    sub: (
                      <>
                        {workedSeg} · 0h free
                      </>
                    ),
                  }
                } else {
                  meta = {
                    c: "var(--sf-green-dark)",
                    bg: "var(--sf-green-soft)",
                    icon: Check,
                    label: `${fmtH(shiftH)}h shift`,
                    sub: cell.bookedMinutes > 0
                      ? (
                        <>
                          {workedSeg} · {fmtH(freeH)}h free
                        </>
                      )
                      : `${fmtH(freeH)}h free`,
                    // Actual free windows — shown as a third line under the
                    // "Xh worked · Yh free" summary so operators can offer
                    // slots to leads without opening the shift modal.
                    windows: freeWindowsStr,
                  }
                }
                const Icon = meta.icon
                return (
                  <div
                    key={di}
                    className="flex-1 flex items-center justify-center"
                    style={{
                      padding: "8px",
                      borderRight: di < 6 ? "1px solid var(--sf-border-soft)" : "none",
                      background: isToday ? "#FAFCFF" : "transparent",
                    }}
                  >
                    <button
                      onClick={() =>
                        onOpenManageShift?.({ teamId: id, dayIdx: di, day: days[di] })
                      }
                      style={{
                        width: "100%",
                        padding: "9px 8px",
                        background: meta.bg,
                        color: meta.c,
                        border: `1px solid ${meta.c}33`,
                        borderRadius: 7,
                        fontSize: 11,
                        fontWeight: 600,
                        textAlign: "center",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 3,
                        cursor: "pointer",
                        fontFamily: "var(--sf-font-ui)",
                        transition: "transform .1s, box-shadow .1s",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.boxShadow = "var(--sf-shadow)"
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.boxShadow = "none"
                      }}
                    >
                      <Icon size={12} />
                      <span style={{ fontVariantNumeric: "tabular-nums" }}>{meta.label}</span>
                      {meta.sub && (
                        <span className="text-[10px] opacity-80">{meta.sub}</span>
                      )}
                      {meta.windows && (
                        <span
                          className="text-[10px] font-semibold"
                          style={{ opacity: 0.9, lineHeight: 1.15, wordBreak: "break-word" }}
                        >
                          {meta.windows}
                        </span>
                      )}
                    </button>
                  </div>
                )
              })}
            </div>
            </React.Fragment>
          )
        })}
      </SfCard>
      </>
      )}
    </div>
  )
}

// ── Availability day view ──────────────────────────────────
// One row per bookable cleaner for the anchored day. Row layout mirrors
// the Schedule tab's DayView so the two views feel like the same
// surface, but each row surfaces the cleaner's shift window, free
// windows, and current booked load instead of a single job.
const AvailabilityDayView = ({
  anchor,
  cleaners,
  jobs,
  cleanerColor,
  onOpenManageShift,
  companyDrivingMinutes = 0,
}) => {
  const day = useMemo(() => startOfDay(anchor), [anchor])
  const dateKey = useMemo(() => formatDateKey(day), [day])
  const isToday = sameDay(day, startOfDay(new Date()))

  // Filter to bookable cleaners only — matches the "availability
  // excludes managers" rule used by LB sync and operator reports.
  const bookable = useMemo(
    () => (cleaners || []).filter(isBookableTeamMember),
    [cleaners]
  )

  const rows = useMemo(() => {
    const dayJobs = (jobs || []).filter((j) => {
      const d = jobStartDateTime(j)
      return d && sameDay(startOfDay(d), day)
    })
    const bookedByCleaner = new Map()
    dayJobs.forEach((j) => {
      const d = jobStartDateTime(j)
      if (!d) return
      const iv = jobIntervalOnDate(d, durationMinutes(j), dateKey)
      if (!iv) return
      assigneesFor(j).forEach((a) => {
        const id = String(a.id)
        if (!bookedByCleaner.has(id)) bookedByCleaner.set(id, { ivs: [], count: 0 })
        bookedByCleaner.get(id).ivs.push(iv)
        bookedByCleaner.get(id).count += 1
      })
    })
    return bookable.map((m) => {
      const id = String(m.id)
      const working = getWorkingIntervals(m.availability, dateKey)
      const booked = bookedByCleaner.get(id) || { ivs: [], count: 0 }
      const driving = memberDrivingMinutes(m.availability, companyDrivingMinutes)
      const noSignal = working === null
      const offDay = Array.isArray(working) && working.length === 0
      const freeIvs = Array.isArray(working)
        ? subtractIntervals(working, padIntervals(booked.ivs, driving))
        : []
      return {
        member: m,
        id,
        working: Array.isArray(working) ? working : [],
        workingMinutes: Array.isArray(working) ? sumIntervalMinutes(working) : 0,
        bookedIvs: booked.ivs,
        bookedCount: booked.count,
        bookedMinutes: sumIntervalMinutes(booked.ivs),
        freeIvs,
        freeMinutes: sumIntervalMinutes(freeIvs),
        offDay,
        noSignal,
      }
    }).sort((a, b) => {
      // Off/no-signal to bottom; everyone else by free time desc.
      const rank = (r) => (r.noSignal ? 2 : r.offDay ? 1 : 0)
      const ra = rank(a), rb = rank(b)
      if (ra !== rb) return ra - rb
      return b.freeMinutes - a.freeMinutes
    })
  }, [bookable, jobs, day, dateKey, companyDrivingMinutes])

  const activeRows = rows.filter((r) => !r.offDay && !r.noSignal)
  const totals = useMemo(() => ({
    onShift: activeRows.length,
    totalBookable: bookable.length,
    freeHours: Math.round(activeRows.reduce((s, r) => s + r.freeMinutes, 0) / 60),
    bookedHours: Math.round(activeRows.reduce((s, r) => s + r.bookedMinutes, 0) / 60),
  }), [activeRows, bookable])

  const fmtMin = (mm) => {
    const h = Math.floor(mm / 60), mi = mm % 60
    const suf = h >= 12 ? "p" : "a"
    const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h
    return mi === 0 ? `${h12}${suf}` : `${h12}:${String(mi).padStart(2, "0")}${suf}`
  }
  const fmtH = (h) => {
    const r = Math.round(h * 10) / 10
    return Number.isInteger(r) ? String(r) : r.toFixed(1)
  }

  if (bookable.length === 0) {
    return (
      <SfCard>
        <div className="py-12 text-center text-[12.5px] text-[var(--sf-ink-3)]">
          No bookable cleaners loaded.
        </div>
      </SfCard>
    )
  }

  return (
    <SfCard padding={0}>
      {/* Header */}
      <div className="flex items-center border-b border-[var(--sf-border-soft)] px-4 py-3">
        <div className="flex-1 min-w-0">
          <div className="text-[13.5px] font-semibold text-[var(--sf-ink)]" style={{ letterSpacing: "-0.005em" }}>
            {day.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}
            {isToday && (
              <span
                className="ml-2 text-[9.5px] font-bold align-middle"
                style={{
                  color: "#fff",
                  background: "var(--sf-blue)",
                  padding: "1px 5px",
                  borderRadius: 3,
                  fontFamily: "var(--sf-font-mono)",
                }}
              >
                TODAY
              </span>
            )}
          </div>
          <div className="text-[11.5px] text-[var(--sf-ink-3)] mt-0.5">
            {totals.onShift} / {totals.totalBookable} on shift
            {totals.freeHours > 0 && ` · ${totals.freeHours}h free`}
            {totals.bookedHours > 0 && ` · ${totals.bookedHours}h booked`}
          </div>
        </div>
      </div>

      {/* Rows */}
      <div>
        {rows.map((r) => {
          const name =
            r.member.name ||
            `${r.member.first_name || ""} ${r.member.last_name || ""}`.trim() ||
            r.member.email ||
            "Cleaner"
          const color = cleanerColor(r.id)
          const freeH = r.freeMinutes / 60
          const bookedH = r.bookedMinutes / 60
          const shiftLabel = r.working.length > 0
            ? r.working.map(([s, e]) => `${fmtMin(s)}–${fmtMin(e)}`).join(", ")
            : null
          const freeWindowLabels = r.freeIvs.map(([s, e]) => `${fmtMin(s)}–${fmtMin(e)}`)
          const isDim = r.offDay || r.noSignal

          let statusLabel = null
          let statusColor = color
          if (r.noSignal) {
            statusLabel = "NO SCHEDULE"
            statusColor = "var(--sf-ink-3)"
          } else if (r.offDay) {
            statusLabel = "OFF"
            statusColor = "var(--sf-ink-3)"
          } else if (r.freeMinutes === 0) {
            statusLabel = "FULLY BOOKED"
            statusColor = "#B91C1C"
          } else if (r.bookedMinutes === 0) {
            statusLabel = "FULLY FREE"
            statusColor = "var(--sf-green)"
          }

          return (
            <button
              key={r.id}
              onClick={() => onOpenManageShift?.({ teamId: r.id, dayIdx: 0, day })}
              className="w-full flex items-stretch gap-3 px-4 py-3 border-b border-[var(--sf-border-soft)] hover:bg-[var(--sf-panel-alt)] transition-colors"
              style={{
                textAlign: "left",
                cursor: "pointer",
                border: "none",
                borderBottom: "1px solid var(--sf-border-soft)",
                opacity: isDim ? 0.55 : 1,
                fontFamily: "var(--sf-font-ui)",
                background: "transparent",
              }}
            >
              {/* Color spine */}
              <div style={{ width: 3, borderRadius: 2, background: statusColor, flexShrink: 0 }} />

              {/* Shift col */}
              <div style={{ width: 110, flexShrink: 0 }}>
                <div
                  className="text-[13px] font-semibold text-[var(--sf-ink)]"
                  style={{ fontVariantNumeric: "tabular-nums", lineHeight: 1.2 }}
                >
                  {shiftLabel || "—"}
                </div>
                {!isDim && (
                  <div className="text-[10.5px] text-[var(--sf-ink-3)] mt-0.5" style={{ fontVariantNumeric: "tabular-nums" }}>
                    {fmtH(r.workingMinutes / 60)}h shift
                  </div>
                )}
              </div>

              {/* Middle: name + badges + free windows */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 min-w-0 flex-wrap">
                  <span className="text-[13.5px] font-semibold text-[var(--sf-ink)] truncate">
                    {name}
                  </span>
                  {statusLabel && (
                    <span
                      className="text-[9px] font-bold flex-shrink-0"
                      style={{
                        color: "#fff",
                        background: statusColor,
                        padding: "1px 5px",
                        borderRadius: 3,
                        fontFamily: "var(--sf-font-mono)",
                        letterSpacing: ".04em",
                      }}
                    >
                      {statusLabel}
                    </span>
                  )}
                  {r.bookedCount > 0 && (
                    <span
                      className="text-[9px] font-bold flex-shrink-0"
                      style={{
                        color: "#B91C1C",
                        background: "#FEE2E2",
                        padding: "1px 5px",
                        borderRadius: 3,
                        fontFamily: "var(--sf-font-mono)",
                      }}
                      title={`${r.bookedCount} job${r.bookedCount === 1 ? "" : "s"} · ${fmtH(bookedH)}h booked`}
                    >
                      {r.bookedCount} JOB{r.bookedCount === 1 ? "" : "S"} · {fmtH(bookedH)}h
                    </span>
                  )}
                </div>
                {freeWindowLabels.length > 0 && (
                  <div
                    className="text-[11.5px] text-[var(--sf-ink-2)] mt-0.5 truncate"
                    style={{ fontVariantNumeric: "tabular-nums" }}
                    title={`Free: ${freeWindowLabels.join(", ")}`}
                  >
                    Free: {freeWindowLabels.join(" · ")}
                  </div>
                )}
              </div>

              {/* Right: free hours + avatar */}
              <div className="flex items-center gap-2 flex-shrink-0">
                {!isDim && (
                  <div className="text-right" style={{ minWidth: 44 }}>
                    <div
                      className="text-[14px] font-bold"
                      style={{
                        color: freeH > 0 ? "var(--sf-green-dark)" : "var(--sf-ink-3)",
                        fontVariantNumeric: "tabular-nums",
                        lineHeight: 1,
                      }}
                    >
                      {fmtH(freeH)}h
                    </div>
                    <div className="text-[10px] text-[var(--sf-ink-3)]">free</div>
                  </div>
                )}
                <SfAvatar initials={sfInitials(name) || "?"} color={color} size={30} />
              </div>
            </button>
          )
        })}
      </div>
    </SfCard>
  )
}

// ── Availability month view ────────────────────────────────
// 7×6 month calendar. Each cell shows an aggregate of how many
// bookable cleaners are on shift that day and how many free hours
// remain after travel buffer. Clicking a cell drops into Day view.
const AvailabilityMonthView = ({
  anchor,
  cleaners,
  jobs,
  companyDrivingMinutes = 0,
  onPickDay,
}) => {
  const monthStart = useMemo(() => {
    const x = new Date(anchor)
    x.setDate(1)
    x.setHours(0, 0, 0, 0)
    return x
  }, [anchor])
  const gridStart = useMemo(() => startOfWeek(monthStart), [monthStart])
  const days = useMemo(
    () => Array.from({ length: 42 }, (_, i) => addDays(gridStart, i)),
    [gridStart]
  )
  const dateKeys = useMemo(() => days.map(formatDateKey), [days])
  const today = startOfDay(new Date())

  const bookable = useMemo(
    () => (cleaners || []).filter(isBookableTeamMember),
    [cleaners]
  )

  // Per-day aggregates. Scans each cleaner's shift + booked intervals
  // once per visible day.
  const byDay = useMemo(() => {
    const bookedByDay = new Map() // dateKey → cleanerId → intervals
    ;(jobs || []).forEach((j) => {
      const d = jobStartDateTime(j)
      if (!d) return
      const dayIdx = days.findIndex((dd) => sameDay(dd, startOfDay(d)))
      if (dayIdx < 0) return
      const dateKey = dateKeys[dayIdx]
      const iv = jobIntervalOnDate(d, durationMinutes(j), dateKey)
      if (!iv) return
      if (!bookedByDay.has(dateKey)) bookedByDay.set(dateKey, new Map())
      const byCleaner = bookedByDay.get(dateKey)
      assigneesFor(j).forEach((a) => {
        const id = String(a.id)
        if (!byCleaner.has(id)) byCleaner.set(id, [])
        byCleaner.get(id).push(iv)
      })
    })
    const out = new Map()
    dateKeys.forEach((dateKey) => {
      let onShift = 0
      let freeMinutes = 0
      let bookedMinutes = 0
      const bookedForDate = bookedByDay.get(dateKey) || new Map()
      bookable.forEach((m) => {
        const id = String(m.id)
        const working = getWorkingIntervals(m.availability, dateKey)
        if (!Array.isArray(working) || working.length === 0) return
        onShift += 1
        const bookedIvs = bookedForDate.get(id) || []
        const driving = memberDrivingMinutes(m.availability, companyDrivingMinutes)
        const freeIvs = subtractIntervals(working, padIntervals(bookedIvs, driving))
        freeMinutes += sumIntervalMinutes(freeIvs)
        bookedMinutes += sumIntervalMinutes(bookedIvs)
      })
      out.set(dateKey, { onShift, freeMinutes, bookedMinutes })
    })
    return out
  }, [bookable, jobs, days, dateKeys, companyDrivingMinutes])

  const fmtH = (mm) => {
    const h = Math.round((mm / 60) * 10) / 10
    return Number.isInteger(h) ? String(h) : h.toFixed(1)
  }
  const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"]

  return (
    <SfCard padding={0}>
      {/* Weekday header */}
      <div className="grid" style={{ gridTemplateColumns: "repeat(7, minmax(0, 1fr))", borderBottom: "1px solid var(--sf-border-soft)" }}>
        {WEEKDAYS.map((w, i) => (
          <div
            key={w}
            className="text-[10.5px] text-[var(--sf-ink-3)] font-bold uppercase"
            style={{
              padding: "10px 12px",
              borderRight: i < 6 ? "1px solid var(--sf-border-soft)" : "none",
              letterSpacing: ".06em",
              textAlign: "center",
              background: "var(--sf-panel-alt)",
            }}
          >
            {w}
          </div>
        ))}
      </div>

      {/* 6-row date grid */}
      <div className="grid" style={{ gridTemplateColumns: "repeat(7, minmax(0, 1fr))" }}>
        {days.map((d, i) => {
          const isOtherMonth = d.getMonth() !== monthStart.getMonth()
          const isTodayCell = sameDay(d, today)
          const dateKey = dateKeys[i]
          const cell = byDay.get(dateKey) || { onShift: 0, freeMinutes: 0, bookedMinutes: 0 }
          const row = Math.floor(i / 7)
          const col = i % 7
          const freeHours = fmtH(cell.freeMinutes)
          return (
            <button
              key={i}
              onClick={() => onPickDay?.(d)}
              style={{
                padding: "8px 10px 10px",
                minHeight: 92,
                textAlign: "left",
                background: isTodayCell
                  ? "var(--sf-blue-soft)"
                  : isOtherMonth
                  ? "var(--sf-panel-alt)"
                  : "var(--sf-panel)",
                borderRight: col < 6 ? "1px solid var(--sf-border-soft)" : "none",
                borderTop: row > 0 ? "1px solid var(--sf-border-soft)" : "none",
                border: "none",
                borderBottom: "none",
                borderLeft: "none",
                cursor: "pointer",
                fontFamily: "var(--sf-font-ui)",
                opacity: isOtherMonth ? 0.55 : 1,
                display: "flex",
                flexDirection: "column",
                gap: 4,
              }}
            >
              <div
                className={isTodayCell ? "text-[13px] font-bold" : "text-[12.5px] font-semibold"}
                style={{
                  color: isTodayCell ? "var(--sf-blue-dark)" : "var(--sf-ink)",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {d.getDate()}
              </div>
              {cell.onShift > 0 ? (
                <>
                  <div
                    className="text-[11px] font-semibold"
                    style={{ color: "var(--sf-ink-2)", lineHeight: 1.2 }}
                  >
                    {cell.onShift} cleaner{cell.onShift === 1 ? "" : "s"}
                  </div>
                  <div
                    className="text-[10.5px] font-bold"
                    style={{
                      color: cell.freeMinutes > 0 ? "var(--sf-green-dark)" : "var(--sf-ink-3)",
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {freeHours}h free
                  </div>
                  {cell.bookedMinutes > 0 && (
                    <div
                      className="text-[10px]"
                      style={{ color: "#B91C1C", fontVariantNumeric: "tabular-nums" }}
                    >
                      {fmtH(cell.bookedMinutes)}h booked
                    </div>
                  )}
                </>
              ) : (
                <div className="text-[10.5px] text-[var(--sf-ink-3)]">—</div>
              )}
            </button>
          )
        })}
      </div>
    </SfCard>
  )
}

// ── Manage shift modal ─────────────────────────────────────

// New modal kind per ADDON_schedule_overlap_and_assign.md Part 2.
// Opened from any Availability cell or daily tile; shows 3 tabs of
// jobs for the selected team+day and surfaces the Assign / Reassign
// / Pull / Unassign actions.
const ManageShiftModal = ({
  open, payload, onClose, jobs, cleaners, cleanerColor,
  resolveName, anchor, onMutated, onOpenJob, onNewJob,
}) => {
  const navigate = useNavigate()
  const [subTab, setSubTab] = useState("assigned")
  const [busy, setBusy] = useState(false)

  // Reset tab whenever payload changes — if launched from an open
  // slot we default to "unassigned"; if from a job we land on the
  // tab matching that job's current assignment.
  useEffect(() => {
    if (!payload) return
    if (payload.openSlot) setSubTab("unassigned")
    else if (payload.jobId) {
      const j = jobs.find((x) => String(x.id) === String(payload.jobId))
      const ids = j ? assigneesFor(j).map((a) => a.id) : []
      if (ids.length === 0) setSubTab("unassigned")
      else if (ids.includes(String(payload.teamId))) setSubTab("assigned")
      else setSubTab("other")
    } else {
      setSubTab("assigned")
    }
  }, [payload, jobs])

  if (!open || !payload) return null
  const teamId = String(payload.teamId)
  const day = payload.day || startOfDay(anchor)
  const team = cleaners.find((m) => String(m.id) === teamId)
  const teamName =
    team?.name ||
    `${team?.first_name || ""} ${team?.last_name || ""}`.trim() ||
    resolveName?.(teamId, "") ||
    `Cleaner ${teamId}`
  const teamColor = cleanerColor(teamId)

  // Day-filtered job buckets
  const dayJobs = jobs.filter((j) => {
    const d = jobStartDateTime(j)
    return d && sameDay(startOfDay(d), day)
  })
  const assigned = dayJobs.filter((j) =>
    assigneesFor(j).some((a) => a.id === teamId)
  )
  const unassigned = dayJobs.filter((j) => assigneesFor(j).length === 0)
  const others = dayJobs.filter(
    (j) => assigneesFor(j).length > 0 && !assigneesFor(j).some((a) => a.id === teamId)
  )

  // If launched from an open slot, narrow Unassigned to jobs whose
  // duration fits the window.
  const slotFiltered = payload.openSlot
    ? unassigned.filter(
        (j) => durationMinutes(j) <= payload.openSlot.end - payload.openSlot.start
      )
    : unassigned

  const counts = { assigned: assigned.length, unassigned: slotFiltered.length, other: others.length }
  const list = subTab === "assigned" ? assigned : subTab === "unassigned" ? slotFiltered : others

  // Working window(s) + free window(s) for this cleaner on this day, so
  // the operator sees when open time exists without leaving the modal.
  const dateKey = formatDateKey(day)
  const workingIvs = getWorkingIntervals(team?.availability, dateKey) || []
  const bookedIvs = assigned
    .map((j) => {
      const sd = jobStartDateTime(j)
      return sd ? jobIntervalOnDate(sd, durationMinutes(j), dateKey) : null
    })
    .filter(Boolean)
  const freeIvs = subtractIntervals(workingIvs, bookedIvs)

  // Assigned-tab timeline: interleave free-slot cards between assigned
  // job rows in chronological order. Lets the operator see when the
  // cleaner is free between cleanings — with exact times.
  const assignedTimeline = (() => {
    if (subTab !== "assigned") return null
    const items = []
    assigned.forEach((j) => {
      const sd = jobStartDateTime(j)
      const start = sd ? sd.getHours() * 60 + sd.getMinutes() : 0
      items.push({ type: "job", start, job: j })
    })
    freeIvs.forEach(([s, e]) => {
      items.push({ type: "free", start: s, end: e })
    })
    items.sort((a, b) => a.start - b.start)
    return items
  })()
  const shiftSummary =
    workingIvs.length === 0
      ? null
      : `Shift ${workingIvs.map(([s, e]) => `${minsToLabel(s)}–${minsToLabel(e)}`).join(" · ")}`
  const freeSummary =
    workingIvs.length === 0
      ? null
      : freeIvs.length === 0
        ? "Fully booked"
        : `Free ${freeIvs.map(([s, e]) => `${minsToLabel(s)}–${minsToLabel(e)}`).join(" · ")}`

  const updateAssignment = async (job, nextPrimaryId) => {
    setBusy(true)
    try {
      // Single-cleaner reassign: PUT /jobs/:id with team_member_id.
      // Multi-cleaner jobs would need /assign-multiple; that path is
      // covered by the dedicated job-detail page.
      await jobsAPI.update(job.id, { team_member_id: nextPrimaryId })
      onMutated?.()
    } catch (e) {
      alert(e?.response?.data?.error || e?.message || "Couldn't update the assignment.")
    } finally {
      setBusy(false)
    }
  }

  const onAssignToTeam = (job) => updateAssignment(job, parseInt(teamId, 10) || teamId)
  const onPullToTeam = (job) => updateAssignment(job, parseInt(teamId, 10) || teamId)
  const onUnassign = (job) => updateAssignment(job, null)

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 100,
        background: "rgba(15,23,42,.4)",
        backdropFilter: "blur(4px)",
        WebkitBackdropFilter: "blur(4px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
        fontFamily: "var(--sf-font-ui)",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 720,
          maxHeight: "85vh",
          background: "var(--sf-panel)",
          borderRadius: 14,
          border: "1px solid var(--sf-border-soft)",
          boxShadow: "var(--sf-shadow-l)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <div
          className="flex items-start gap-3"
          style={{ padding: "14px 18px", borderBottom: "1px solid var(--sf-border-soft)" }}
        >
          <div
            className="w-9 h-9 rounded-md flex items-center justify-center flex-shrink-0"
            style={{ background: `${teamColor}1a`, color: teamColor }}
          >
            <CalendarIcon size={16} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[14px] font-bold text-[var(--sf-ink)]">
              Manage shift ·{" "}
              <button
                type="button"
                onClick={() => {
                  onClose?.()
                  navigate(`/team/${teamId}`)
                }}
                title={`Open ${teamName.split(" ")[0]}'s team setup — edit availability`}
                style={{
                  background: "transparent",
                  border: "none",
                  padding: 0,
                  font: "inherit",
                  color: "var(--sf-blue-dark)",
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 2,
                  textDecoration: "underline",
                  textDecorationColor: "var(--sf-blue-dark)",
                  textDecorationThickness: 1,
                  textUnderlineOffset: 3,
                }}
              >
                {teamName}
                <ChevronRight size={13} style={{ opacity: 0.8, marginTop: 1 }} />
              </button>
            </div>
            <div className="text-[11.5px] text-[var(--sf-ink-3)] mt-px">
              {day.toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" })}
              {payload.openSlot && (
                <> · open slot {minsToLabel(payload.openSlot.start)}–{minsToLabel(payload.openSlot.end)}</>
              )}
            </div>
            {(shiftSummary || freeSummary) && (
              <div
                className="text-[11px] mt-1"
                style={{ display: "flex", gap: 10, flexWrap: "wrap" }}
              >
                {shiftSummary && (
                  <span style={{ color: "var(--sf-ink-3)" }}>{shiftSummary}</span>
                )}
                {freeSummary && (
                  <span
                    style={{
                      color: freeIvs.length === 0
                        ? "var(--sf-amber-dark)"
                        : "var(--sf-green-dark)",
                      fontWeight: 600,
                    }}
                  >
                    {freeSummary}
                  </span>
                )}
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              background: "transparent",
              border: "none",
              padding: 4,
              cursor: "pointer",
              color: "var(--sf-ink-3)",
            }}
          >
            <X size={16} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-[var(--sf-border-soft)] px-2" style={{ background: "var(--sf-panel-alt)" }}>
          {[
            { id: "assigned",   label: `Assigned to ${teamName.split(" ")[0]}`, count: counts.assigned },
            { id: "unassigned", label: "Unassigned",  count: counts.unassigned },
            { id: "other",      label: "Other teams", count: counts.other },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setSubTab(t.id)}
              style={{
                padding: "10px 12px",
                background: "transparent",
                border: "none",
                borderBottom: subTab === t.id ? "2px solid var(--sf-blue)" : "2px solid transparent",
                fontSize: 12.5,
                fontWeight: subTab === t.id ? 700 : 500,
                color: subTab === t.id ? "var(--sf-blue-dark)" : "var(--sf-ink-2)",
                cursor: "pointer",
                fontFamily: "var(--sf-font-ui)",
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              {t.label}
              <span
                style={{
                  fontSize: 10.5,
                  fontWeight: 700,
                  background: subTab === t.id ? "var(--sf-blue-soft)" : "var(--sf-panel-soft)",
                  color: subTab === t.id ? "var(--sf-blue-dark)" : "var(--sf-ink-3)",
                  padding: "1px 6px",
                  borderRadius: 99,
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {t.count}
              </span>
            </button>
          ))}
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: "auto" }}>
          {subTab === "assigned" ? (
            assignedTimeline.length === 0 ? (
              <div className="py-10 text-center text-[12.5px] text-[var(--sf-ink-3)]">
                {workingIvs.length === 0
                  ? `${teamName.split(" ")[0]} is not on shift this day.`
                  : `No jobs currently assigned to ${teamName.split(" ")[0]} on this day.`}
              </div>
            ) : (
              assignedTimeline.map((item, i) => {
                const isLast = i === assignedTimeline.length - 1
                if (item.type === "job") {
                  return (
                    <ShiftJobRow
                      key={`j-${item.job.id}`}
                      job={item.job}
                      isLast={isLast}
                      teamColor={teamColor}
                      cleanerColor={cleanerColor}
                      resolveName={resolveName}
                      subTab={subTab}
                      teamName={teamName}
                      busy={busy}
                      highlight={String(item.job.id) === String(payload.jobId)}
                      onOpen={() => onOpenJob?.(item.job)}
                      onAssign={() => onAssignToTeam(item.job)}
                      onPull={() => onPullToTeam(item.job)}
                      onUnassign={() => onUnassign(item.job)}
                    />
                  )
                }
                return (
                  <FreeSlotRow
                    key={`f-${item.start}-${item.end}`}
                    start={item.start}
                    end={item.end}
                    isLast={isLast}
                    teamName={teamName}
                    onNewJob={() => onNewJob?.(teamId, { day, start: item.start, end: item.end })}
                  />
                )
              })
            )
          ) : list.length === 0 ? (
            <div className="py-10 text-center text-[12.5px] text-[var(--sf-ink-3)]">
              {subTab === "unassigned"
                ? payload.openSlot
                  ? "No unassigned jobs fit this open slot."
                  : "Every job today already has a cleaner."
                : "No jobs assigned to other teams on this day."}
            </div>
          ) : (
            list.map((j, i) => (
              <ShiftJobRow
                key={j.id}
                job={j}
                isLast={i === list.length - 1}
                teamColor={teamColor}
                cleanerColor={cleanerColor}
                resolveName={resolveName}
                subTab={subTab}
                teamName={teamName}
                busy={busy}
                highlight={String(j.id) === String(payload.jobId)}
                onOpen={() => onOpenJob?.(j)}
                onAssign={() => onAssignToTeam(j)}
                onPull={() => onPullToTeam(j)}
                onUnassign={() => onUnassign(j)}
              />
            ))
          )}
        </div>

        {/* Footer */}
        <div
          className="flex items-center"
          style={{
            padding: "10px 14px",
            background: "var(--sf-panel-alt)",
            borderTop: "1px solid var(--sf-border-soft)",
          }}
        >
          <span className="text-[11px] text-[var(--sf-ink-3)] italic">
            Tap a job to manage · drag to move time (coming soon)
          </span>
          <div className="flex-1" />
          <SfButton variant="ghost" size="sm" onClick={onClose}>Close</SfButton>
          <SfButton
            variant="primary"
            size="sm"
            icon={Plus}
            onClick={() => onNewJob?.(teamId)}
          >
            New job for {teamName.split(" ")[0]}
          </SfButton>
        </div>
      </div>
    </div>
  )
}

const ShiftJobRow = ({
  job, isLast, teamColor, cleanerColor, resolveName, subTab, teamName,
  busy, highlight, onOpen, onAssign, onPull, onUnassign,
}) => {
  const d = jobStartDateTime(job)
  const rangeLabel = d ? formatJobRange(job) : "—"
  const dur = durationMinutes(job)
  const customer = customerLabelForJob(job)
  const service = job.service_name || job.service?.name || job.title || "Service"
  const addr = job.service_address || job.customer_address || ""
  const value = parseFloat(job.total || job.service_price || 0) || 0
  const live = isLiveJob(job)
  const assignees = assigneesFor(job)
  const currentTeamId = assignees[0]?.id
  const currentTeamColor = currentTeamId ? cleanerColor(currentTeamId) : "#DC2626"
  const currentTeamName =
    currentTeamId ? resolveName?.(currentTeamId, "") || `Cleaner ${currentTeamId}` : null

  return (
    <div
      style={{
        display: "flex",
        alignItems: "stretch",
        gap: 12,
        padding: "12px 16px",
        borderBottom: isLast ? "none" : "1px solid var(--sf-border-soft)",
        background: highlight ? "var(--sf-blue-soft)" : "transparent",
        borderLeft: `3px solid ${subTab === "assigned" ? teamColor : currentTeamColor}`,
      }}
    >
      <div style={{ width: 82, textAlign: "center", flexShrink: 0 }}>
        <div
          className="text-[12px] font-bold text-[var(--sf-ink)]"
          style={{ fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}
        >
          {rangeLabel}
        </div>
        <div className="text-[10px] text-[var(--sf-ink-3)] mt-px">{dur}m</div>
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-[13px] font-bold text-[var(--sf-ink)]">{customer}</span>
          <span
            className="text-[10.5px] text-[var(--sf-ink-3)]"
            style={{ fontFamily: "var(--sf-font-mono)" }}
          >
            #{job.id}
          </span>
          {job.is_recurring && (
            <SfTag color="var(--sf-purple)" bg="var(--sf-purple-soft)">↻ Recurring</SfTag>
          )}
          {live && (
            <SfTag color="var(--sf-green-dark)" bg="var(--sf-green-soft)">Live</SfTag>
          )}
        </div>
        <div className="text-[11.5px] text-[var(--sf-ink-2)] mt-0.5 truncate">
          {service}
        </div>
        {addr && (
          <div className="text-[11px] text-[var(--sf-ink-3)] mt-px inline-flex items-center gap-1 max-w-full">
            <MapPin size={11} className="flex-shrink-0" />
            <span className="truncate">{addr}</span>
          </div>
        )}
        {currentTeamId && subTab !== "assigned" && (
          <div className="text-[11px] text-[var(--sf-ink-3)] mt-1 inline-flex items-center gap-1">
            <span
              className="w-1.5 h-1.5 rounded-full"
              style={{ background: currentTeamColor }}
            />
            Currently: {currentTeamName}
          </div>
        )}
      </div>
      <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
        <div
          className="text-[13px] font-semibold text-[var(--sf-ink)]"
          style={{ fontVariantNumeric: "tabular-nums" }}
        >
          ${Math.round(value).toLocaleString()}
        </div>
        <div className="flex items-center gap-1.5">
          <SfButton variant="ghost" size="sm" onClick={onOpen}>
            View
          </SfButton>
          {subTab === "assigned" && (
            <>
              <SfButton
                variant="ghost"
                size="sm"
                onClick={onUnassign}
                disabled={busy}
                style={{ color: "var(--sf-red-dark)" }}
              >
                Unassign
              </SfButton>
            </>
          )}
          {subTab === "unassigned" && (
            <SfButton variant="primary" size="sm" onClick={onAssign} disabled={busy}>
              Assign
            </SfButton>
          )}
          {subTab === "other" && (
            <SfButton variant="secondary" size="sm" onClick={onPull} disabled={busy}>
              Pull to {teamName.split(" ")[0]}
            </SfButton>
          )}
        </div>
      </div>
    </div>
  )
}

// ── Free-slot card (in manage-shift modal, Assigned tab) ────
//
// A gap between assigned cleanings shown inline in the timeline so the
// operator sees exactly when the cleaner is free without having to
// eyeball job start/end times.
const FreeSlotRow = ({ start, end, isLast, teamName, onNewJob }) => {
  const durMin = end - start
  const h = Math.floor(durMin / 60)
  const m = durMin % 60
  const durLabel = m === 0 ? `${h}h` : h === 0 ? `${m}m` : `${h}h ${m}m`
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "10px 16px",
        borderBottom: isLast ? "none" : "1px solid var(--sf-border-soft)",
        background: "var(--sf-green-soft)",
        borderLeft: "3px solid var(--sf-green)",
      }}
    >
      <div style={{ width: 82, textAlign: "center", flexShrink: 0 }}>
        <div
          className="text-[12px] font-bold"
          style={{
            color: "var(--sf-green-dark)",
            fontVariantNumeric: "tabular-nums",
            whiteSpace: "nowrap",
          }}
        >
          {minsToLabel(start)}–{minsToLabel(end)}
        </div>
        <div className="text-[10px] text-[var(--sf-ink-3)] mt-px">{durLabel}</div>
      </div>
      <div className="min-w-0 flex-1">
        <div
          className="text-[12.5px] font-semibold"
          style={{ color: "var(--sf-green-dark)" }}
        >
          Free window
        </div>
        <div className="text-[11px] text-[var(--sf-ink-2)] mt-0.5">
          {durLabel} open on {teamName.split(" ")[0]}'s shift
        </div>
      </div>
      <div className="flex-shrink-0">
        <SfButton variant="primary" size="sm" icon={Plus} onClick={onNewJob}>
          Book job
        </SfButton>
      </div>
    </div>
  )
}

// ── Unassigned view ────────────────────────────────────────

const UnassignedView = ({ jobs, onJobClick, onAssign }) => {
  const list = useMemo(
    () =>
      jobs
        .filter((j) => !isCancelledJob(j) && assigneesFor(j).length === 0)
        .sort((a, b) => {
          const ad = jobStartDateTime(a)?.getTime() || 0
          const bd = jobStartDateTime(b)?.getTime() || 0
          return ad - bd
        }),
    [jobs]
  )

  if (list.length === 0) {
    return (
      <SfCard>
        <div className="py-12 text-center text-[12.5px] text-[var(--sf-ink-3)]">
          Every scheduled job has a cleaner assigned.
        </div>
      </SfCard>
    )
  }

  return (
    <SfCard padding={0}>
      <div className="px-4 py-3 border-b border-[var(--sf-border-soft)]">
        <div className="text-[13.5px] font-semibold text-[var(--sf-ink)]">Unassigned jobs</div>
        <div className="text-[11.5px] text-[var(--sf-ink-3)] mt-px">
          {list.length} job{list.length === 1 ? "" : "s"} need a cleaner
        </div>
      </div>
      {list.map((j, i) => {
        const d = jobStartDateTime(j)
        const dayLabel = d
          ? d.toLocaleDateString("en-US", { month: "short", day: "numeric" })
          : "—"
        const timeLabel = d ? formatJobTime(j) : "—"
        const value = parseFloat(j.total || j.service_price || 0) || 0
        const customer = customerLabelForJob(j)
        const service = j.service_name || j.service?.name || j.title || "Service"
        const addr = j.service_address || j.customer_address || ""
        return (
          <div
            key={j.id}
            className="flex items-center gap-3 px-4 py-3 hover:bg-[var(--sf-panel-alt)] transition-colors"
            style={{
              borderBottom: i < list.length - 1 ? "1px solid var(--sf-border-soft)" : "none",
              cursor: "pointer",
            }}
            onClick={() => onJobClick?.(j)}
          >
            <div style={{ width: 56, textAlign: "center" }} className="flex-shrink-0">
              <div
                className="text-[13px] font-bold text-[var(--sf-ink)]"
                style={{ fontVariantNumeric: "tabular-nums" }}
              >
                {timeLabel}
              </div>
              <div className="text-[10.5px] text-[var(--sf-ink-3)]">{dayLabel}</div>
            </div>
            <SfAvatar initials={sfInitials(customer)} color="var(--sf-ink)" size={32} />
            <div className="min-w-0 flex-1">
              <div className="text-[13px] font-semibold text-[var(--sf-ink)] truncate">
                {customer} <span className="text-[var(--sf-ink-3)] font-medium">· {service}</span>
              </div>
              {addr && (
                <div
                  className="text-[11.5px] text-[var(--sf-ink-3)] mt-px inline-flex items-center gap-1"
                  style={{ maxWidth: "100%" }}
                >
                  <MapPin size={11} className="flex-shrink-0" />
                  <span className="truncate">{addr}</span>
                </div>
              )}
            </div>
            <SfTag color="var(--sf-red-dark)" bg="var(--sf-red-soft)">
              Unassigned
            </SfTag>
            <div
              className="text-[13px] font-semibold text-[var(--sf-ink)]"
              style={{ fontVariantNumeric: "tabular-nums" }}
            >
              ${Math.round(value).toLocaleString()}
            </div>
            <SfButton
              variant="primary"
              size="sm"
              icon={Plus}
              onClick={(e) => { e.stopPropagation(); onAssign?.(j) }}
            >
              Assign
            </SfButton>
          </div>
        )
      })}
    </SfCard>
  )
}

export default ScheduleV2
